import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { X, Send, Zap, User as UserIcon, Bot, Building2, UserCircle, ChevronLeft, ChevronRight, Minus, Loader2, Target, AlertCircle, Mail, Linkedin, Phone, CheckCircle2, Sparkles, ExternalLink, Info, Video, FileText, BarChart3, RefreshCw, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import ChatAnswerWithBlocks from '@/components/ChatAnswerWithBlocks';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { createPageUrl } from '@/utils';
import { sanitizeId } from '@/components/utils/id';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Textarea } from '@/components/ui/textarea';
import AccountSelector from './AccountSelector';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { AccountFinal } from '@/api/entities';
import { PersonFinal } from '@/api/entities';
import { User as UserEntity } from '@/api/entities';
import {
  logNeoRecommendation,
  logBriefShown,
  extractAccountId,
  extractPersonId,
  getCurrentTenantId
} from '@/components/utils/analytics';
import { resolvePlatformUiLanguage, useTranslation } from '@/components/utils/translations';
import {
  MEETING_TYPE_DEFS,
  createDefaultGreetingMessage,
  getMeetingTypeLabel,
  getSuggestionChips,
  isDefaultGreetingMessage,
} from '@/lib/neoChatI18n';
import PersonAvatar from '@/components/PersonAvatar';
import {
  fetchPrimaryProcurementCategoriesForTenant,
  fetchLiveDealsFromProcurementApi,
  filterActiveLiveDeals,
  getProcurementDeadline,
  getDealCategoryDisplay,
} from '@/lib/procurementTenantLiveDeals';
import { resolveEffectiveTenant, normalizeTenantId } from '@/lib/tenantUtils';
import { personaBelongsToAccount } from '@/lib/accountPoolJoin';
import { isSysExamProductContextTenant } from '@/lib/accountTargetProducts';
import {
  buildBotMessageFromChatbotResponse,
  buildChatbotAskBody,
  clearStoredChatSessionId,
  consumeChatbotAskStream,
  loadStoredChatSessionId,
  normalizeChatBlocks,
  resolveChatbotAccountId,
  chatbotAskAccountIdFromSources,
  isAccountRowUuid,
  saveStoredChatSessionId,
  accountFromResolvedAccount,
  postChatbotAccountRequest,
  requestChipFromNotProvisioned,
  isWorkspaceAccountChipAction,
} from '@/lib/chatbotAsk';
import { buildMeetingPrepBotMessage } from '@/lib/meetingPrepBlocks';
import { requestMeetingPrep } from '@/lib/api/meetingPrep';
import { addMarketAccount } from '@/lib/marketExperienceApi';
import { isPilotCapReachedError, pilotErrorMessage } from '@/lib/pilotMode';
import ChatBotMessageExtras from '@/components/ChatBotMessageExtras';
import ChatBotAnswerFeedback from '@/components/ChatBotAnswerFeedback';
import ChatMorningBriefMessage from '@/components/ChatMorningBriefMessage';
import NextBestMoveMessage from '@/components/accounts/NextBestMoveCard';
import {
  fetchAccountSalesMap,
  resolveNextBestMove,
  sanitizeNextBestMoveForDisplay,
} from '@/lib/salesMapApi';
import ChatTriggerNudgesMessage from '@/components/ChatTriggerNudgesMessage';
import {
  clampPanelWidth,
  nearestStop,
  PANEL_STOPS,
  persistPanelWidth,
  readStoredPanelWidth,
  widthForStop,
} from '@/lib/chatPanelWidth';
import {
  buildMorningBriefMessage,
  buildBriefTargetPageUrl,
  markChatbotBriefViewed,
  postChatbotRecommendationAction,
} from '@/lib/chatbotBrief';
import {
  buildTriggerNudgesMessage,
  markChatbotTriggersViewed,
} from '@/lib/chatbotTriggers';
import {
  clearLegacyConversationStorage,
  fetchChatbotBriefCached,
  fetchChatbotTriggersCached,
  filterBriefResponseItems,
  handleChatbotTenantSwitch,
  loadConversationMessages,
  proactiveMessageMatchesTenant,
  saveConversationMessages,
  stripForeignProactiveMessages,
  ADMIN_TENANT_CHANGE_EVENT,
} from '@/lib/chatbotCache';
import {
  buildComposeViaPersonaPagePath,
  navigateToNeogtmDeepLink,
} from '@/lib/chatbotDeepLinks';


/**
 * [2026-10-04] Stable fallback trace id, hoisted to module scope.
 *
 * `clientTraceId` was state inside NeoChatPanel, but two call sites far earlier in this file used
 * it as the fallback in `currentUser?.id || clientTraceId` while being OUTSIDE that component's
 * scope — so the identifier was simply undefined there (eslint no-undef, NeoChatPanel.jsx:1369 and
 * :2043). Because `||` short-circuits, it threw only when `currentUser?.id` was falsy: the
 * defensive fallback crashed with a ReferenceError at precisely the moment it was needed, taking
 * out the Meeting Prep flow.
 *
 * Both the component state and those call sites now read this one lazily-created value. The id is
 * now per PAGE LOAD rather than per NeoChatPanel mount, which is if anything more useful for
 * tracing a session, and identical for the overwhelmingly common single-mount case.
 */
let _clientTraceId = null;
function getClientTraceId() {
  if (!_clientTraceId) {
    _clientTraceId = `trace-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }
  return _clientTraceId;
}

const API_BASE_URL = (() => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'https://api.neogtm.com';
    }
  }
  return 'https://api.neogtm.com';
})();
const DEFAULT_ACCESS = 'public';

/**
 * Resolve active tenant for the chat panel. Returns null while unresolved so we
 * never fetch against a hardcoded fallback (e.g. instructure) during hydration.
 */
function resolveChatPanelTenant(adminActive, currentTenantProp, user) {
  const userTenant = normalizeTenantId(user?.tenant_id);
  const propTenant = normalizeTenantId(currentTenantProp);
  const adminResolved = normalizeTenantId(adminActive);
  const fallback = userTenant || propTenant || adminResolved || null;

  if (!adminResolved && !propTenant && !userTenant && !user?.id) {
    return null;
  }

  if (user?.role === 'admin' && !adminResolved && !propTenant) {
    return null;
  }

  if (!fallback) return null;

  return resolveEffectiveTenant(adminResolved, propTenant, user, fallback);
}

// Add retry helper with exponential backoff
const fetchWithRetry = async (fetchFn, maxRetries = 3, baseDelay = 2000) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fetchFn();
    } catch (error) {
      // Check for rate limit status from fetch response or error message
      // Assuming entity.list() methods might throw errors with a 'status' property or 'message' string including '429'
      const isRateLimit = error.status === 429 || error.message?.includes('429');

      if (isRateLimit && attempt < maxRetries) {
        const delay = baseDelay * Math.pow(2, attempt - 1) + Math.random() * 1000; // Add jitter
        console.log(`⏳ Rate limit hit, waiting ${delay.toFixed(0)}ms before retry (attempt ${attempt}/${maxRetries})...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        // If not a rate limit error, or max retries reached for rate limit, re-throw
        throw error;
      }
    }
  }
};

// Media query hook to determine desktop vs mobile
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = React.useState(
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches : true
  );
  React.useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener?.('change', handler);
    // Safari fallback
    mq.addListener?.(handler);
    return () => {
      mq.removeEventListener?.('change', handler);
      mq.removeListener?.(handler);
    };
  }, []);
  return isDesktop;
}

const MEETING_TYPE_ICONS = {
  discovery: Phone,
  demo: Video,
  proposal: FileText,
  negotiation: BarChart3,
  renewal: RefreshCw,
  qbr: Calendar,
};

const buildOutreachQuestion = ({ account, persona, config, userContext }) => {
  const channel = config?.channel || 'email';
  const tone = (config?.tone || 'relationship').replace(/_/g, ' ');
  const type = config?.message_type === 'sequence' ? '3-message sequence' : 'single message';
  const person = persona ? `${persona.first_name} ${persona.last_name}` : 'the target contact';
  const company = account?.account_name || '';
  let contextStr = '';
  if (config?.context_type === 'custom' && userContext) {
    contextStr = ` Use this context verbatim: "${userContext}".`;
  } else if (config?.context_type && config.context_type !== 'auto') {
    // Convert underscore_case to Title Case for display
    const contextTypeLabel = config.context_type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    contextStr = ` Context focus: ${contextTypeLabel}.`;
  } else {
    contextStr = ' Auto-select the most relevant context from available signals.';
  }
  return `Draft a ${type} via ${channel} to ${person} at ${company} in a ${tone} tone.${contextStr}`;
};

function isProactiveChatMessage(message) {
  return (
    message?.type === 'morning_brief' ||
    message?.type === 'trigger_nudges' ||
    message?.type === 'next_best_move'
  );
}

/** True once the user has sent anything — proactive brief/trigger chips should step aside. */
function conversationHasUserTurn(messages) {
  return Array.isArray(messages) && messages.some((m) => m?.sender === 'user');
}

let __msgSeq = 0;
const nextMessageId = () => `msg-${Date.now()}-${++__msgSeq}`;

// Normalize messages to ensure stable IDs
const normalizeMsgs = (arr=[]) => arr.map((m) => ({ ...m, id: m.id ?? nextMessageId() }));

const normalizeAccountName = (name) => {
  if (!name) return '';
  return name.toLowerCase().trim();
};

async function loadChatData(tenantId) {
  console.log('🔄 Loading fresh chat data for tenant:', tenantId);

  try {
    // Use static imports that are already at the top of the file
    const [allAccounts, allPeople] = await Promise.all([
      fetchWithRetry(() => AccountFinal.list()),
      fetchWithRetry(() => PersonFinal.list())
    ]);

    const tenantMatches = (record) => {
      // Hong Kong accounts to hide for instructure tenant
      const INSTRUCTURE_HIDDEN_HK_ACCOUNTS = ['hk_001_001', 'hk_001_011'];
      const INSTRUCTURE_HIDDEN_HK_NAMES = [
        'City University of Hong Kong (CityU)',
        'The Hong Kong University of Science and Technology (HKUST)'
      ];

      // Check account_id
      if (record.account_id && INSTRUCTURE_HIDDEN_HK_ACCOUNTS.includes(record.account_id) && tenantId === 'instructure') {
        return false;
      }

      // Check account_name
      if (record.account_name && INSTRUCTURE_HIDDEN_HK_NAMES.includes(record.account_name) && tenantId === 'instructure') {
        return false;
      }

      // Check institution_name (for momentum scores)
      if (record.institution_name && INSTRUCTURE_HIDDEN_HK_NAMES.includes(record.institution_name) && tenantId === 'instructure') {
        return false;
      }

      if (tenantId === 'smowl') return record.tenant_id === 'smowl';
      if (record.tenant_id === tenantId) return true;
      if (record.tenant_id === 'global') return true;
      if (tenantId === 'instructure' && (!record.tenant_id || record.tenant_id === 'default')) return true;
      if (Array.isArray(record.visible_to_tenants) && record.visible_to_tenants.includes(tenantId)) return true;
      if (record.tenant_overrides && record.tenant_overrides[tenantId]) return true;
      if (tenantId === 'instructure' && record.tenant_id === 'clairelabs') return true;
      if (tenantId === 'diambra' &&
          (record.tenant_id === 'smowl' || record.tenant_id === 'instructure' || record.tenant_id === 'clairelabs' || record.tenant_id === 'base44' || record.tenant_id === 'default' || record.tenant_id === 'diambra' || !record.tenant_id)) {
        return true;
      }
      if (tenantId === 'edqwest') return record.tenant_id === 'edqwest';
      if (tenantId === 'faria') return record.tenant_id === 'edqwest';
      if (tenantId === 'calmedica') return record.tenant_id === 'calmedica' || record.tenant_id === 'global';
      return false;
    };

    const accounts = allAccounts
      .filter(acc => acc.is_visible !== false && tenantMatches(acc))
      .map(acc => {
        if (acc.tenant_overrides && acc.tenant_overrides[tenantId]) {
          return { ...acc, ...acc.tenant_overrides[tenantId] };
        }
        return acc;
      });

    const visibleAccountNames = new Set(
      accounts.map(acc => normalizeAccountName(acc.account_name)).filter(Boolean)
    );

    const people = allPeople
      .filter(person => {
        if (person.is_visible === false) return false;
        if (!person.account_name) return false;
        const personAccountName = normalizeAccountName(person.account_name);
        if (!visibleAccountNames.has(personAccountName)) {
          return false;
        }

        return tenantMatches(person);
      })
      .map(person => {
        if (person.tenant_overrides && person.tenant_overrides[tenantId]) {
          return { ...person, ...person.tenant_overrides[tenantId] };
        }
        return person;
      });

    console.log('✅ Chat data loaded for tenant:', tenantId, {
      accounts: accounts.length,
      people: people.length,
      sampleAccounts: accounts.slice(0, 5).map(a => ({ name: a.account_name, country: a.country, tenant_id: a.tenant_id })),
      samplePeople: people.slice(0, 5).map(p => ({ name: `${p.first_name} ${p.last_name}`, account: p.account_name, tenant_id: p.tenant_id })),
      hongKongAccounts: accounts.filter(a => a.country === 'Hong Kong').length,
      southAfricaAccounts: accounts.filter(a => a.country === 'South Africa').length,
      hongKongPeople: people.filter(p => {
        const acc = accounts.find(a => normalizeAccountName(a.account_name) === normalizeAccountName(p.account_name));
        return acc && acc.country === 'Hong Kong';
      }).length,
      southAfricaPeople: people.filter(p => {
        const acc = accounts.find(a => normalizeAccountName(a.account_name) === normalizeAccountName(p.account_name));
        return acc && acc.country === 'South Africa';
      }).length
    });

    // Accounts/people are always loaded fresh per tenant (no sessionStorage cache).

    return { accounts, people };
  } catch (error) {
    console.error('❌ Error loading chat data:', error);
    throw error;
  }
}

const inferMeetingPrepProduct = (account) => {
  if (!account) return null;

  const targetProducts = Array.isArray(account.target_products)
    ? account.target_products.filter(Boolean)
    : [];

  if (targetProducts.length === 1) {
    return String(targetProducts[0]).toUpperCase();
  }

  return null;
};

const inferMeetingPrepCategory = (product, tenantId) => {
  const tenant = String(tenantId || '').toLowerCase();
  const normalizedProduct = String(product || '').toUpperCase();

  if (tenant === 'learnwise') {
    if (normalizedProduct === 'TUTOR') return 'AI & Virtual Assistants';
    if (normalizedProduct === 'SUPPORT') return 'AI & Virtual Assistants';
    if (normalizedProduct === 'FEEDBACK') return 'Marking / Grading Platforms';
  }

  if (isSysExamProductContextTenant(tenant)) {
    if (normalizedProduct === 'SYS') return 'Course & Teaching Evaluation';
    if (normalizedProduct === 'EXAM') return 'Online Assessment & e-Exam Platforms';
  }

  return null;
};

const MeetingPrepFlow = ({ accounts, people, onGenerate, onCancel, isLoadingData, tenantId, initialAccount }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(() => (initialAccount ? 'type' : 'account'));
  const [selectedAccount, setSelectedAccount] = useState(initialAccount ?? null);
  const [selectedMeetingType, setSelectedMeetingType] = useState(null);
  const [selectedAttendees, setSelectedAttendees] = useState([]);

  useEffect(() => {
    if (initialAccount) {
      setSelectedAccount(initialAccount);
      setStep('type');
    }
  }, [initialAccount]);

  const accountPeople = useMemo(() => {
    if (!selectedAccount) return [];
    return people
      .filter((p) => personaBelongsToAccount(p, selectedAccount))
      .sort((a, b) => (b.persona_fit_score || 0) - (a.persona_fit_score || 0));
  }, [selectedAccount, people]);

  const handleAccountSelect = (account) => {
    setSelectedAccount(account);
    setStep('type');
  };

  const handleMeetingTypeSelect = (type) => {
    setSelectedMeetingType(type);
    setStep('attendees');
  };

  const toggleAttendee = (personId) => {
    setSelectedAttendees(prev => 
      prev.includes(personId) 
        ? prev.filter(id => id !== personId)
        : [...prev, personId]
    );
  };

  const handleGenerate = () => {
    const inferredProduct = inferMeetingPrepProduct(selectedAccount);
    const inferredCategory = inferMeetingPrepCategory(inferredProduct, tenantId);

    onGenerate({
      account: selectedAccount,
      meetingType: selectedMeetingType,
      attendeeIds: selectedAttendees,
      product: inferredProduct,
      category: inferredCategory
    });
  };

  const handleSkipAttendees = () => {
    const inferredProduct = inferMeetingPrepProduct(selectedAccount);
    const inferredCategory = inferMeetingPrepCategory(inferredProduct, tenantId);

    onGenerate({
      account: selectedAccount,
      meetingType: selectedMeetingType,
      attendeeIds: [],
      product: inferredProduct,
      category: inferredCategory
    });
  };

  return (
        <div className="p-3 space-y-2.5 bg-gray-50 rounded-lg w-[380px] overflow-hidden h-[380px] flex flex-col">      <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
        <span className={step === 'account' ? 'text-[#00C2C2] font-medium' : ''}>{t('neoChatStepAccount')}</span>
        <ChevronLeft className="w-3 h-3 rotate-180" />
        <span className={step === 'type' ? 'text-[#00C2C2] font-medium' : ''}>{t('neoChatStepType')}</span>
        <ChevronLeft className="w-3 h-3 rotate-180" />
        <span className={step === 'attendees' ? 'text-[#00C2C2] font-medium' : ''}>{t('neoChatStepAttendees')}</span>
      </div>

      {step === 'account' && (
  <div className="flex flex-col flex-1 min-h-0 space-y-2">
    <h3 className="font-semibold text-sm text-gray-900 flex-shrink-0">{t('neoChatSelectAccount')}</h3>
    {isLoadingData ? (
      <div className="flex flex-col items-center justify-center py-8 flex-1">
        <Loader2 className="w-6 h-6 animate-spin text-[#00C2C2] mb-2" />
        <p className="text-xs text-gray-500">{t('neoChatLoadingAccounts')}</p>
      </div>
    ) : (
      <Command className="rounded-lg border shadow-md flex-1 min-h-0 overflow-hidden">
        <CommandInput placeholder={t('neoChatSearchAccounts')} />
        <CommandList>
          <CommandEmpty>{t('neoChatNoAccountsFound')}</CommandEmpty>
          <CommandGroup>
            {accounts.map((account) => (
              <CommandItem key={account.id} onSelect={() => handleAccountSelect(account)} className="flex items-center gap-2">
                {account.logo_url ? (
                  <img src={account.logo_url} alt={account.account_name} className="w-5 h-5 object-contain flex-shrink-0" />
                ) : (
                  <Building2 className="w-5 h-5 text-gray-400 flex-shrink-0" />
                )}
                <span className="truncate">{account.account_name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    )}
    <Button onClick={onCancel} variant="outline" className="w-full">
      {t('cancel')}
    </Button>
  </div>
)}

      {step === 'type' && (
        <>
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold text-sm text-gray-900 truncate">{t('neoChatSelectMeetingType')}</h3>
            <Button variant="ghost" size="sm" onClick={() => setStep('account')} className="text-[#00C2C2] hover:bg-[#00C2C2]/10 h-7 px-2 flex-shrink-0">
              <ChevronLeft className="w-4 h-4 mr-1" />{t('neoChatBack')}
            </Button>
          </div>
          <p className="text-xs text-gray-500 truncate">{t('neoChatMeetingWith', { accountName: selectedAccount?.account_name })}</p>
          <div className="grid grid-cols-2 gap-2">
            {MEETING_TYPE_DEFS.map((type) => {
              const Icon = MEETING_TYPE_ICONS[type.value];
              return (
                <button
                  key={type.value}
                  onClick={() => handleMeetingTypeSelect(type.value)}
                  className="p-2.5 border rounded-lg hover:bg-white hover:border-[#00C2C2] transition-colors text-left"
                >
                  <Icon className="w-4 h-4 text-[#00C2C2] mb-1.5" />
                  <div className="text-xs font-medium text-gray-900 break-words leading-tight">{t(type.labelKey)}</div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {step === 'attendees' && (
        <div className="flex flex-col flex-1 min-h-0 space-y-2">
          <Button variant="ghost" size="sm" onClick={() => setStep('type')} className="text-[#00C2C2] hover:bg-[#00C2C2]/10 h-7 px-2 flex-shrink-0 self-start">
            <ChevronLeft className="w-4 h-4 mr-1" />{t('neoChatBack')}
          </Button>
          <h3 className="font-semibold text-sm text-gray-900">{t('neoChatSelectAttendeesOptional')}</h3>
          <p className="text-xs text-gray-500 truncate">
            {selectedMeetingType && getMeetingTypeLabel(t, selectedMeetingType)} • {selectedAccount?.account_name}
          </p>

          {isLoadingData && (
            <div className="flex flex-col items-center justify-center py-8 flex-1">
              <Loader2 className="w-6 h-6 animate-spin text-[#00C2C2] mb-2" />
              <p className="text-xs text-gray-500">{t('neoChatLoadingPeople')}</p>
            </div>
          )}

          {!isLoadingData && selectedAttendees.length > 0 && (
            <div className="flex flex-wrap gap-1 max-w-full overflow-x-hidden mt-2 flex-shrink-0">
              {selectedAttendees.map(id => {
                const person = accountPeople.find(p => p.id === id || p.person_id === id);
                if (!person) return null;
                return (
                  <Badge key={id} variant="secondary" className="gap-0.5 text-xs px-1.5 py-0.5 max-w-full">
                    <span className="truncate text-xs">{person.first_name} {person.last_name}</span>
                    <button onClick={() => toggleAttendee(id)} className="hover:text-red-600 flex-shrink-0 text-sm">×</button>
                  </Badge>
                );
              })}
            </div>
          )}
          {!isLoadingData && (
          <div className="flex-1 overflow-y-auto space-y-0.5 border rounded bg-white p-1 mt-2 max-h-[280px]">
            {accountPeople.length === 0 ? (
              <p className="text-xs text-gray-500 text-center py-2">{t('neoChatNoPeopleFound')}</p>
            ) : (
              accountPeople.map((person) => {
                const personId = person.person_id || person.id;
                const isSelected = selectedAttendees.includes(personId);
                return (
                  <label
                    key={personId}
                    className="flex items-center gap-1.5 p-1 hover:bg-gray-50 rounded cursor-pointer min-w-0"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleAttendee(personId)}
                      className="w-3 h-3 text-[#00C2C2] rounded flex-shrink-0"
                    />
                    <PersonAvatar
                      personId={person.person_id || person.id}
                      hostedPhotoUrl={person.hosted_photo_url}
                      linkedinPhotoUrl={person.profile_photo_url}
                      firstName={person.first_name}
                      lastName={person.last_name}
                      size="xs"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-900 truncate leading-tight">{person.first_name} {person.last_name}</div>
                      {person.role_title && (
                        <div className="text-[10px] text-gray-500 truncate leading-tight">{person.role_title}</div>
                      )}
                    </div>
                  </label>
                );
              })
            )}
          </div>
          )}

          {!isLoadingData && (
          <div className="flex gap-2 mt-2 flex-shrink-0">
            <Button onClick={handleSkipAttendees} variant="outline" className="flex-1 h-9 text-sm">
              {t('neoChatSkip')}
            </Button>
            <Button 
              onClick={handleGenerate} 
              className="flex-1 h-9 text-sm bg-[#00C2C2] hover:bg-[#00A3A3]"
            >
              {t('neoChatGenerate')}{selectedAttendees.length > 0 && ` (${selectedAttendees.length})`}
            </Button>
          </div>
          )}
        </div>
      )}  
    </div>
  );
};

const OutreachConfigPicker = ({ onSelect, onCancel, accounts, people, preselectedAccount, preselectedPersona }) => {
  const { t } = useTranslation();
  const [selectedAccountId, setSelectedAccountId] = useState(preselectedAccount?.id || null);
  const [selectedPersonaId, setSelectedPersonaId] = useState(preselectedPersona?.id || preselectedPersona?.person_id || null);

  const filteredPeople = useMemo(() => {
    if (!selectedAccountId || !Array.isArray(people)) return [];
    const selectedAccount = accounts?.find(a => a.id === selectedAccountId);
    if (!selectedAccount) return [];
    // Filter by account_name or account_id to link people to the selected account
    return people.filter((p) => personaBelongsToAccount(p, selectedAccount));
  }, [selectedAccountId, accounts, people]);

  // If a preselected persona doesn't belong to the initially selected account,
  // or if the account changes, clear the persona selection.
  useEffect(() => {
    if (selectedAccountId && selectedPersonaId) {
      const personaBelongsToAccount = filteredPeople.some(p => (p.id === selectedPersonaId || p.person_id === selectedPersonaId));
      if (!personaBelongsToAccount) {
        setSelectedPersonaId(null);
      }
    }
  }, [selectedAccountId, selectedPersonaId, filteredPeople]);

  // Auto-advance when both are selected
  useEffect(() => {
    if (selectedAccountId && selectedPersonaId) {
      const account = accounts?.find(a => a.id === selectedAccountId);
      const persona = people?.find(p => (p.id === selectedPersonaId || p.person_id === selectedPersonaId));

      if (account && persona) {
        // Small delay to ensure state is updated
        setTimeout(() => {
          onSelect(account, persona);
        }, 100);
      }
    }
  }, [selectedAccountId, selectedPersonaId, accounts, people, onSelect]);

  return (
    <div className="p-4 space-y-4 bg-gray-50 rounded-lg">
      <h3 className="font-semibold text-sm text-gray-900">{t('neoChatSelectAccountAndPerson')}</h3>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">{t('neoChatAccountLabel')}</label>
        <Select value={selectedAccountId || ''} onValueChange={setSelectedAccountId}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t('neoChatSelectAccountPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {accounts?.map(acc => (
              <SelectItem key={acc.id} value={acc.id}>
                <div className="flex items-center gap-2">
                  {acc.logo_url && (
                    <img src={acc.logo_url} alt={acc.account_name} className="w-4 h-4 object-contain" />
                  )}
                  <span>{acc.account_name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedAccountId && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('neoChatPersonLabel')}</label>
          <Select value={selectedPersonaId || ''} onValueChange={setSelectedPersonaId} disabled={filteredPeople.length === 0}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder={filteredPeople.length === 0 ? t('neoChatNoPeopleForAccount') : t('neoChatSelectPersonPlaceholder')} />
            </SelectTrigger>
          <SelectContent>
              {filteredPeople.map(person => {
                const personId = person.person_id || person.id;
                const fullName = person.first_name && person.last_name
                  ? `${person.first_name} ${person.last_name}`
                  : person.full_name || 'Unknown';

                return (
                  <SelectItem key={personId} value={personId}>
                    <div className="flex items-center gap-2">
                    <PersonAvatar
                        personId={person.person_id || person.id}
                        hostedPhotoUrl={person.hosted_photo_url}
                        linkedinPhotoUrl={person.profile_photo_url}
                        firstName={person.first_name}
                        lastName={person.last_name}
                        size="xs"
                      />
                      <div className="flex flex-col">
                        <span className="text-sm">{fullName}</span>
                        {person.role_title && (
                          <span className="text-xs text-gray-500">{person.role_title}</span>
                        )}
                      </div>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex gap-2">
        <Button onClick={onCancel} variant="outline" className="flex-1">
          {t('cancel')}
        </Button>
      </div>
    </div>
  );
};


const OutreachConfigurationForm = ({
  account,
  persona,
  handleSend,
  onBack,
  isLoading,
  isSending,
  typingRef,
  scrollToBottom,
  focusInputIfNeeded,
  currentUser,
  initialUserContext = '',
}) => {
  const { t } = useTranslation();
  const prefilledContext = String(initialUserContext || '').trim();
  const [config, setConfig] = React.useState({
      channel: 'email',
      tone: 'relationship',
      message_type: 'single',
      context_type: prefilledContext ? 'custom' : 'auto',
  });
  const [userContext, setUserContext] = React.useState(prefilledContext);
  const [showUserContextInput, setShowUserContextInput] = React.useState(Boolean(prefilledContext));

  useEffect(() => {
    setShowUserContextInput(config.context_type === 'custom');
    if (config.context_type !== 'custom') {
      setUserContext(''); // Clear custom context if type changes
    }
  }, [config.context_type]);

  const handleGenerate = () => {
      // Pass the config and userContext together
      handleSend('', { config: { ...config, userContext } });
  };

  return (
      <div className="p-4 space-y-4">
          <div className="text-center">
              <p className="text-sm font-medium text-[#0A1A2F] mb-1">
                  {t('neoChatConfigureOutreachTo', { name: `${persona.first_name} ${persona.last_name}` })}
              </p>
              <p className="text-xs text-gray-500">{t('neoChatAtAccount', { accountName: account.account_name })}</p>
          </div>

          <Button
              variant="ghost" // Updated variant
              size="sm"
              onClick={onBack}
              className="text-[#00C2C2] hover:text-[#00A3A3] hover:bg-[#00C2C2]/10 px-2 h-8" // Updated classes
          >
              <ChevronLeft className="w-4 h-4" />
              {t('neoChatBackToPeople')}
          </Button>

          <div className="space-y-4">
              <div>
                  <label className="text-sm font-medium text-[#0A1A2F] block mb-2">{t('neoChatChannelLabel')}</label>
                  <div className="space-y-2">
                      {[
                          { value: 'email', label: 'Email' },
                          { value: 'linkedin', label: 'LinkedIn Message' },
                          { value: 'call_script', label: 'Call Script' }
                      ].map(option => (
                          <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                              <input
                                  type="radio"
                                  name="channel"
                                  value={option.value}
                                  checked={config.channel === option.value}
                                  onChange={(e) => setConfig({...config, channel: e.target.value})}
                                  className="text-[#00C2C2]"
                              />
                              <span className="text-sm">{option.label}</span>
                          </label>
                      ))}
                  </div>
              </div>

              <div>
                  <label className="text-sm font-medium text-[#0A1A2F] block mb-2">{t('neoChatToneLabel')}</label>
                  <div className="space-y-2">
                      {[
                              { value: 'relationship', label: 'Relationship Building', description: 'Warm, curious, collaborative' },
                              { value: 'thought_leadership', label: 'Thought Leadership', description: 'Authoritative, data-driven' },
                              { value: 'challenge_status_quo', label: 'Challenge Status Quo', description: 'Provocative, insightful, disruptive' } // Moved here
                      ].map(option => (
                          <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                              <input
                                  type="radio"
                                  name="tone"
                                  value={option.value}
                                  checked={config.tone === option.value}
                                  onChange={(e) => setConfig({...config, tone: e.target.value})}
                                  className="text-[#00C2C2]"
                              />
                              <span className="text-sm">{option.label}</span>
                          </label>
                      ))}
                  </div>
              </div>

              <div>
                  <label className="text-sm font-medium text-[#0A1A2F] block mb-2">{t('neoChatMessageTypeLabel')}</label>
                  <div className="space-y-2">
                      {[
                          { value: 'single', label: 'Single Message' },
                          { value: 'sequence', label: '3-Touch Campaign' }
                      ].map(option => (
                          <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                              <input
                                  type="radio"
                                  name="message_type"
                                  value={option.value}
                                  checked={config.message_type === option.value}
                                  onChange={(e) => setConfig({...config, message_type: e.target.value})}
                                  className="text-[#00C2C2]"
                              />
                              <span className="text-sm">{option.label}</span>
                          </label>
                      ))}
                  </div>
              </div>

              <div>
                  <label className="text-sm font-medium text-[#0A1A2F] block mb-2">{t('neoChatContextTriggerLabel')}</label>
                  <select
                      value={config.context_type}
                      onChange={(e) => setConfig({...config, context_type: e.target.value})}
                      onMouseDown={() => { if (!typingRef.current) scrollToBottom(true); }}
                      onFocus={() => { if (!typingRef.current) scrollToBottom(true); }}
                      className="w-full p-2 border rounded-md text-sm"
                  >
                      <option value="auto">🎯 Auto-select best context</option>
                      <option value="persona_initiative">👤 Persona initiative/interest</option>
                      <option value="institutional_change">🏛️ Recent institutional change</option>
                      <option value="procurement_signal">📋 Procurement signal</option>
                      <option value="strategic_priority">📊 Strategic priority</option>
                      {/* Removed "challenge_status_quo" from here */}
                      <option value="custom">✍️ Provide custom context</option>
                  </select>
              </div>

              {showUserContextInput && (
                <div className="mt-3">
                    <Input
                        type="text"
                        placeholder={t('neoChatOutreachContextPlaceholder')}
                        value={userContext}
                        onChange={(e) => setUserContext(e.target.value)}
                        onFocus={() => { if (!typingRef.current) scrollToBottom(true); }}
                        onBlur={() => { if (!typingRef.current) scrollToBottom(true); }}
                        className="w-full"
                    />
                </div>
              )}

              <Button
                  onClick={handleGenerate}
                  className="w-full bg-[#00C2C2] hover:bg-[#00A3A3]"
                  disabled={isLoading || isSending}
              >
                  {isSending ? t('neoChatGenerating') : (config.message_type === 'sequence' ? t('neoChatGenerateSequence') : t('neoChatGenerateMessage'))}
              </Button>
          </div>
      </div>
  );
};


function ChatAnchorChip({ account, onClear, t }) {
  const name = account?.account_name;
  if (!name) return null;
  return (
    <div className="mt-2 flex items-center gap-1.5 min-w-0">
      <span className="text-xs text-gray-300 truncate">
        {t('neoChatAskingAbout', { accountName: name })}
      </span>
      <button
        type="button"
        onClick={onClear}
        aria-label={t('neoChatClearAccount')}
        className="shrink-0 rounded p-0.5 text-gray-400 hover:text-white hover:bg-white/10"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

function lookupAccountName(accountId, accounts, context) {
  if (!accountId) return '';
  const fromContext =
    context?.account_name
    || context?.preselectedAccount?.account_name
    || context?.account?.account_name
    || context?.pageAccount?.account_name
    || '';
  if (fromContext) return fromContext;
  const hit = (accounts || []).find(
    (a) => a.account_id === accountId || a.id === accountId || String(a.id) === String(accountId),
  );
  return hit?.account_name || '';
}

function ChatPendingBubble({ progress, botClassName }) {
  const label = typeof progress?.label === 'string' && progress.label.trim() ? progress.label : null;
  const pct = typeof progress?.pct === 'number' && Number.isFinite(progress.pct) ? progress.pct : null;
  const clamped = pct == null ? null : Math.max(0, Math.min(100, pct));
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-full bg-[#00C2C2] flex items-center justify-center flex-shrink-0">
        <Bot className={botClassName} />
      </div>
      <div className="p-3 rounded-lg bg-gray-100">
        {label ? (
          <div className="space-y-2 min-w-[10rem] max-w-[16rem]">
            <p className="text-sm text-[#0A1A2F]">{label}</p>
            {clamped != null && (
              <div
                className="h-1.5 w-full rounded-full bg-gray-200 overflow-hidden"
                role="progressbar"
                aria-valuenow={Math.round(clamped)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={label}
              >
                <div
                  className="h-full bg-[#00C2C2] motion-safe:transition-[width] motion-safe:duration-300 motion-reduce:transition-none"
                  style={{ width: `${clamped}%` }}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse delay-75"></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse delay-150"></div>
          </div>
        )}
      </div>
    </div>
  );
}

// Hoisted Mobile Chat View Component
export const MobileChatView = React.memo(function MobileChatView(props) {
  const { t } = useTranslation();
  const {
    messages, isSending, isLoading, clarification, accounts, people,
    currentUser, input, setInput, composingRef, typingRef,
    contentRef, bottomRef, mobileScrollRef, inputRef,
    handleChipClick, handleSend, handleExecutePlaybook,
    handleAccountSelect, handlePersonaSelect,
    setClarification, scrollToBottom, focusInputIfNeeded, onClose,
    setMessages, lastOutreachContext, isLoadingData,
    setIsSending, commonHeaders, tenantId, chatSessionId = null,
    handleNewChat, onDisambiguationSelect, onProactiveChipClick, onChatDeepLinkClick,
    onSuggestedNextClick, suggestionChips, eventTracking, accountPageId,
    selectedAccount, onClearAnchor,
  } = props;

  const [meetingPrepProgress, setMeetingPrepProgress] = useState(null);
  const showProactiveChips = !conversationHasUserTurn(messages);

  const inputBarRef = useRef(null);

  // Dynamic bottom padding based on input bar height
  useEffect(() => {
    const el = inputBarRef.current;
    const content = contentRef.current;
    if (!el || !content) return;

    const ro = new ResizeObserver(() => {
      const h = el.getBoundingClientRect().height;
      content.style.paddingBottom = `calc(${h}px + env(safe-area-inset-bottom))`;
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [contentRef]);

  return (
    <motion.div
      initial={false}
      layout={false}
      animate={{ y: 0 }}
      exit={{ y: '100%' }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="fixed inset-0 bg-white z-50 flex flex-col neo-chat-panel"
      style={{ maxHeight: '100dvh', userSelect: 'text', WebkitUserSelect: 'text' }}
    >
      <div className="p-4 border-b bg-[#0A1A2F] text-white flex-shrink-0">
        <div className="flex justify-between items-center gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Zap className="w-5 h-5 text-[#00C2C2] shrink-0" />
            <h3 className="font-semibold text-sm truncate">{t('neoChatAssistantTitle')}</h3>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleNewChat}
              className="text-gray-300 hover:text-white h-8 px-2 text-xs"
            >
              {t('neoChatNewChat')}
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose} className="text-gray-400 hover:text-white">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {clarification?.type !== 'meeting_prep' ? (
          <ChatAnchorChip account={selectedAccount} onClear={onClearAnchor} t={t} />
        ) : null}
      </div>

      <ScrollArea className="flex-1 p-4" ref={mobileScrollRef} style={{ userSelect: 'text', WebkitUserSelect: 'text' }}>
        <div
          ref={contentRef}
          className="space-y-4 pr-3"
          onMouseDownCapture={(e) => {
            const target = e.target;
            if (!(target instanceof Element)) return;
            if (target.closest('button, a, input, textarea, [role="textbox"], [data-cmdk-root], [data-radix-popper-content]')) return;
            if (!typingRef.current) focusInputIfNeeded();
          }}
          onClick={onChatDeepLinkClick}
          style={{ userSelect: 'text', WebkitUserSelect: 'text' }}
        >
          {isLoadingData ? ( // Conditional rendering for loading state
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#00C2C2] mb-4" />
              <p className="text-sm text-gray-500">Loading accounts and people...</p>
            </div>
          ) : (
            <>
              {messages.map((msg, index) => {
                const isLastBotMessage = msg.sender === 'bot' && index === messages.length - 1;
                const isOutreachMessage = msg.isOutreachMessage;

                return (
                  <div
                    key={msg.id || msg.text}
                    data-chat-message-id={msg.id}
                    className={`flex items-start gap-3 ${msg.sender === 'user' ? 'justify-end' : ''}`}
                    style={{ userSelect: 'text', WebkitUserSelect: 'text' }}
                  >
                    {msg.sender !== 'user' && (
                      <div className="w-8 h-8 rounded-full bg-[#00C2C2] flex items-center justify-center flex-shrink-0">
                        <Bot className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div
                      className={`p-3 rounded-lg break-words hyphens-auto ${
                        msg.sender === 'user' ? 'max-w-[85%]' : 'flex-1 min-w-0'
                      } ${msg.sender === 'bot' ? 'bg-gray-100 text-[#0A1A2F]' : msg.sender === 'user' ? 'bg-[#0A1A2F] text-white' : 'bg-blue-100 border border-blue-200 text-blue-900'}`}
                      style={{ userSelect: 'text', WebkitUserSelect: 'text', cursor: 'text' }}
                    >
                      {msg.type === 'morning_brief' ? (
                        <ChatMorningBriefMessage
                          message={msg}
                          onChipClick={onProactiveChipClick}
                          disabled={isSending}
                          eventTracking={eventTracking}
                          showChips={showProactiveChips}
                        />
                      ) : msg.type === 'trigger_nudges' ? (
                        <ChatTriggerNudgesMessage
                          message={msg}
                          onChipClick={onProactiveChipClick}
                          disabled={isSending}
                          showChips={showProactiveChips}
                        />
                      ) : msg.type === 'next_best_move' ? (
                        <NextBestMoveMessage
                          nbm={msg.nbm}
                          accountId={msg.accountId ?? accountPageId}
                          people={people}
                          eventTracking={eventTracking}
                        />
                      ) : (
                        <>
                      <ChatAnswerWithBlocks
                        text={msg.text}
                        blocks={msg.sender === 'user' ? null : msg.blocks}
                        sender={msg.sender}
                        linkRegistry={msg.linkRegistry}
                        people={people}
                      />
                      {msg.sender === 'user' ? null : (
                      <ChatBotMessageExtras
                        message={msg}
                        onCandidateSelect={onDisambiguationSelect}
                        onSuggestedNextClick={onSuggestedNextClick}
                        disabled={isSending}
                      />
                      )}
                      <ChatBotAnswerFeedback
                        message={msg}
                        tenantId={tenantId}
                        userId={currentUser?.id}
                        sessionId={chatSessionId}
                        headers={commonHeaders}
                        disabled={isSending}
                      />
                      {msg.isPlaybook && (
                        <Button
                            onClick={() => {
                                const userMessageText = `Execute playbook: "${msg.metadata.playbook_name}"`;
                                setMessages(prev => [...prev, { sender: 'user', text: userMessageText, id: nextMessageId() }]);
                                handleExecutePlaybook(msg.metadata);
                            }}
                            className="mt-3 w-full bg-[#00C2C2] hover:bg-[#00A3A3]"
                            size="sm"
                        >
                            <Zap className="w-4 h-4 mr-2" />
                            Execute Playbook
                        </Button>
                      )}
                      {isLastBotMessage && isOutreachMessage && lastOutreachContext && (
                        <Button
                            onClick={() => {
                                setClarification({
                                    type: 'configure_outreach',
                                    account: lastOutreachContext.account,
                                    persona: lastOutreachContext.persona
                                });
                                if (!typingRef.current) scrollToBottom(true);
                                focusInputIfNeeded();
                            }}
                            variant="outline"
                            className="mt-3 w-full border-[#00C2C2] text-[#00C2C2] hover:bg-[#00C2C2]/5"
                            size="sm"
                        >
                            Generate Another Message
                        </Button>
                      )}
                        </>
                      )}
                    </div>
                    {msg.sender === 'user' && (
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {currentUser?.profile_photo_url ? (
                            <img src={currentUser.profile_photo_url} alt="You" className="w-full h-full object-cover" />
                        ) : (
                            <UserIcon className="w-4 h-4 text-gray-600" />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {isSending && !messages.some((m) => m.isStreaming) && (
                <ChatPendingBubble progress={meetingPrepProgress} botClassName="w-4 h-4 text-white" />
              )}

              {clarification?.type === 'account' && (
                <div className="p-3" tabIndex={-1}>
                  <p className="text-sm font-medium mb-2 text-center text-[#0A1A2F]">Which account would you like to use?</p>
                  <Command className="rounded-lg border shadow-md">
                    <CommandInput placeholder={t('neoChatSearchAccounts')} />
                    <CommandList>
                      {isLoadingData ? ( // Use isLoadingData here
                        <CommandEmpty>
                          <div className="space-y-2 p-4">
                            <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
                            <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
                            <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
                          </div>
                        </CommandEmpty>
                      ) : accounts.length === 0 ? (
                        <CommandEmpty>No accounts found.</CommandEmpty>
                      ) : (
                        <CommandGroup>
                          {accounts.map((account) => (
                            <CommandItem key={account.id} onSelect={() => handleAccountSelect(account, clarification?.originalPrompt)} className="flex items-center gap-2">
                              {account.logo_url ? (
                                <img src={account.logo_url} alt={account.account_name} className="w-5 h-5 object-contain" />
                              ) : (
                                <Building2 className="w-5 h-5 text-gray-400" />
                              )}
                              <span>{account.account_name}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </div>
              )}

              {clarification?.type === 'persona' && (
                <div className="p-3" tabIndex={-1}>
                  <p className="text-sm font-medium mb-2 text-center text-[#0A1A2F]">
                    Which person at {clarification.account.account_name}?
                  </p>
                   <Button
                     variant="ghost" // Updated variant
                     size="sm"
                     onClick={() => {
                       // This should go back to the general outreach selection (OutreachConfigPicker)
                       setClarification({ type: 'select_outreach_target', preselectedAccount: null, preselectedPersona: null }); // Clear all and go to picker
                       if (!typingRef.current) scrollToBottom(true);
                       focusInputIfNeeded();
                     }}
                     className="text-[#00C2C2] hover:text-[#00A3A3] hover:bg-[#00C2C2]/10 px-2 h-8" // Updated classes
                   >
                    <ChevronLeft className="w-4 h-4 mr-1" /> {/* Added icon */}
                    Back to people selection
                  </Button>
                  <Command className="rounded-lg border shadow-md mt-2">
                    <CommandInput placeholder={t('neoChatSearchPeople')} />
                    <CommandList>
                      <CommandEmpty>
                        {isLoadingData ? ( // Use isLoadingData here
                          <p>Loading people...</p>
                        ) : (
                          <p>No people found for this account.</p>
                        )}
                      </CommandEmpty>
                      <CommandGroup>
                        {people.filter((p) => personaBelongsToAccount(p, clarification.account)).map((person) => (
                          <CommandItem key={person.id} onSelect={() => handlePersonaSelect(person)} className="flex items-center gap-2">
                            <PersonAvatar
                              personId={person.person_id || person.id}
                              hostedPhotoUrl={person.hosted_photo_url}
                              linkedinPhotoUrl={person.profile_photo_url}
                              firstName={person.first_name}
                              lastName={person.last_name}
                              size="xs"
                            />
                            <div className="flex flex-col">
                              <span>{person.first_name} {person.last_name}</span>
                              {person.role_title && (
                                <span className="text-xs text-gray-500">{person.role_title}</span>
                              )}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </div>
              )}
              {clarification?.type === 'select_outreach_target' && (
                <OutreachConfigPicker
                    accounts={accounts}
                    people={people}
                    isLoadingData={isLoadingData}
                    preselectedAccount={clarification.preselectedAccount}
                    preselectedPersona={clarification.preselectedPersona}
                    onSelect={(selectedAccount, selectedPersona) => {
                        setClarification((prev) => ({
                            type: 'configure_outreach',
                            account: selectedAccount,
                            persona: selectedPersona,
                            prefilledOutreachContext: prev?.prefilledOutreachContext,
                        }));
                        if (!typingRef.current) scrollToBottom(true);
                        focusInputIfNeeded();
                    }}
                    onCancel={() => {
                        setClarification(null);
                        if (!typingRef.current) scrollToBottom(true);
                        focusInputIfNeeded();
                    }}
                />
              )}

              {clarification?.type === 'meeting_prep' && (
                <MeetingPrepFlow
                  accounts={accounts}
                  people={people}
                  isLoadingData={isLoadingData}
                  tenantId={tenantId}
                  initialAccount={clarification?.initialAccount}
                  onGenerate={async ({ account, meetingType, attendeeIds, product, category }) => {
                    setClarification(null);
                    setIsSending(true);
                    setMeetingPrepProgress(null);
                    
                    const userMessage = {
                      sender: 'user',
                      text: t('neoChatPrepareMeetingUser', {
                        meetingType: getMeetingTypeLabel(t, meetingType),
                        accountName: account.account_name,
                      }),
                      id: nextMessageId()
                    };
                    setMessages(prev => [...prev, userMessage]);
                    
                    try {
                      if (!product) {
                        console.warn('⚠️ Meeting prep product could not be inferred from account', {
                          tenantId,
                          accountName: account?.account_name,
                          accountId: account?.account_id || account?.id
                        });
                      }

                      console.log('🎯 Calling meeting-prep API:', {
                        url: `${API_BASE_URL}/api/meeting-prep`,
                        account: account.account_name,
                        meetingType,
                        tenantId,
                        product,
                        category,
                        language: resolvePlatformUiLanguage(currentUser, tenantId)
                      });

                      const data = await requestMeetingPrep({
                        apiBase: API_BASE_URL,
                        headers: {
                          'Content-Type': 'application/json',
                          ...commonHeaders,
                        },
                        body: {
                          account_id: account.account_id || account.id,
                          account_name: account.account_name,
                          meeting_type: meetingType,
                          attendee_ids: attendeeIds,
                          tenant_id: tenantId,
                          product,
                          product_context: product,
                          category,
                          language: resolvePlatformUiLanguage(currentUser, tenantId),
                          ...(clarification?.salesMapPayload
                            ? { sales_map: clarification.salesMapPayload }
                            : {}),
                        },
                        onProgress: (progress) => {
                          setMeetingPrepProgress(progress);
                          if (!typingRef.current) scrollToBottom(true);
                        },
                      });
                      
                      const botMessage = buildMeetingPrepBotMessage(data);
                      setMessages(prev => [...prev, botMessage]);
                      
                      logNeoRecommendation({
                        tenantId: getCurrentTenantId(currentUser, tenantId),
                        userId: currentUser?.id || getClientTraceId(),
                        accountId: extractAccountId(account),
                        accountName: account?.account_name,
                        recommendationType: 'meeting_prep',
                        source: 'chatbot',
                        metadata: {
                          meeting_type: meetingType,
                          attendees_count: attendeeIds.length
                        }
                      }).catch(err => console.error('Analytics error:', err));
                      
                    } catch (error) {
                      console.error('Error generating meeting prep:', error);
                      
                      let errorText = error.message;
                      if (error.name === 'AbortError') {
                        errorText = t('neoChatErrorMeetingPrepTimeout');
                      }
                      
                      const errorMessage = {
                        sender: 'bot',
                        text: t('neoChatErrorMeetingPrep', { error: errorText }),
                        id: nextMessageId()
                      };
                      setMessages(prev => [...prev, errorMessage]);
                    } finally {
                      setMeetingPrepProgress(null);
                      setIsSending(false);
                    }
                  }}
                  onCancel={() => {
                    setClarification(null);
                    if (!typingRef.current) scrollToBottom(true);
                    focusInputIfNeeded();
                  }}
                />
              )}

              {clarification?.type === 'configure_outreach' && (
                <OutreachConfigurationForm
                  account={clarification.account}
                  persona={clarification.persona}
                  handleSend={handleSend}
                  onBack={() => {
                      setClarification({
                        type: 'select_outreach_target',
                        preselectedAccount: null, // Clear previous selection when going back
                        preselectedPersona: null  // Clear previous selection when going back
                      });
                      if (!typingRef.current) scrollToBottom(true);
                      focusInputIfNeeded();
                  }}
                  isLoading={isLoading}
                  isSending={isSending}
                  typingRef={typingRef}
                  scrollToBottom={scrollToBottom}
                  focusInputIfNeeded={focusInputIfNeeded}
                  currentUser={currentUser}
                  initialUserContext={clarification.prefilledOutreachContext}
                />
              )}
            </>
          )}

          <div ref={bottomRef} />
        </div>
      </ScrollArea>

      <div ref={inputBarRef} className="p-4 border-t bg-white flex-shrink-0">
        {/* Input bar is only visible when not in a picker/config state */}
        {!(clarification?.type === 'select_outreach_target' || clarification?.type === 'configure_outreach' || clarification?.type === 'account' || clarification?.type === 'persona' || clarification?.type === 'meeting_prep') && (
          <>
            <div className="flex flex-wrap gap-2 mb-3">
                {suggestionChips.map(chip => (
                    <Button key={chip.label} variant="outline" size="sm" className="text-xs h-8" onClick={() => handleChipClick(chip.prompt, chip.requires)}>
                        {chip.label}
                    </Button>
                ))}
            </div>
            <div className="flex items-center gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    if (composingRef.current) return;
                    e.preventDefault();
                    handleSend();
                  }
                }}
                onCompositionStart={() => { composingRef.current = true; }}
                onCompositionEnd={() => { composingRef.current = false; }}
                onFocus={() => { typingRef.current = true; }}
                onBlur={() => { typingRef.current = false; }}
                placeholder={t('neoChatInputPlaceholder')}
                disabled={isSending}
                className="text-base"
              />
              <Button onClick={() => handleSend()} disabled={isSending} className="bg-[#00C2C2] hover:bg-[#00A3A3] px-4">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
});

// Hoisted Desktop Chat View Component
export const DesktopChatView = React.memo(function DesktopChatView(props) {
  const { t } = useTranslation();
  const {
    messages, isSending, isLoading, clarification, accounts, people,
    currentUser, input, setInput, composingRef, typingRef,
    contentRef, bottomRef, desktopScrollRef, inputRef,
    handleChipClick, handleSend, handleExecutePlaybook,
    handleAccountSelect, handlePersonaSelect,
    setClarification, scrollToBottom, focusInputIfNeeded, onClose,
    setMessages, lastOutreachContext, isLoadingData,
    setIsSending, commonHeaders, tenantId, chatSessionId = null,
    handleNewChat, onDisambiguationSelect, onProactiveChipClick, onChatDeepLinkClick,
    onSuggestedNextClick, suggestionChips, eventTracking, accountPageId,
    selectedAccount, onClearAnchor,
  } = props;

  const [meetingPrepProgress, setMeetingPrepProgress] = useState(null);
  const showProactiveChips = !conversationHasUserTurn(messages);
  const inputBarRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(() => readStoredPanelWidth());
  const [panelStop, setPanelStop] = useState(() => nearestStop(readStoredPanelWidth()));
  const resizeRef = useRef({ active: false, startX: 0, startWidth: 0, raf: null });

  const applyPanelStop = useCallback((stop) => {
    const nextWidth = clampPanelWidth(widthForStop(stop));
    setPanelStop(stop);
    setPanelWidth(nextWidth);
    persistPanelWidth(nextWidth);
  }, []);

  const atMinStop = panelStop === 'min';
  const atMaxStop = panelStop === 'full';

  const shrinkPanel = useCallback(() => {
    const idx = PANEL_STOPS.indexOf(panelStop);
    if (idx <= 0) return;
    applyPanelStop(PANEL_STOPS[idx - 1]);
  }, [applyPanelStop, panelStop]);

  const expandPanel = useCallback(() => {
    const idx = PANEL_STOPS.indexOf(panelStop);
    if (idx < 0 || idx >= PANEL_STOPS.length - 1) return;
    applyPanelStop(PANEL_STOPS[idx + 1]);
  }, [applyPanelStop, panelStop]);

  useEffect(() => {
    const onResize = () => {
      setPanelWidth((w) => {
        const clamped = clampPanelWidth(w);
        setPanelStop(nearestStop(clamped));
        return clamped;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== '\\') return;
      if (e.target instanceof Element && e.target.closest('input, textarea, [contenteditable="true"]')) return;
      e.preventDefault();
      applyPanelStop(panelStop === 'full' ? 'default' : 'full');
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [applyPanelStop, panelStop]);

  const applyPanelWidth = useCallback((next) => {
    const clamped = clampPanelWidth(next);
    setPanelWidth(clamped);
    setPanelStop(nearestStop(clamped));
  }, []);

  const startResize = useCallback((e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    resizeRef.current = {
      active: true,
      startX: e.clientX,
      startWidth: panelWidth,
    };
    document.body.style.userSelect = 'none';
  }, [panelWidth]);

  const onResizePointerMove = useCallback((e) => {
    if (!resizeRef.current.active) return;
    const delta = resizeRef.current.startX - e.clientX;
    const next = resizeRef.current.startWidth + delta;
    if (resizeRef.current.raf) cancelAnimationFrame(resizeRef.current.raf);
    resizeRef.current.raf = requestAnimationFrame(() => applyPanelWidth(next));
  }, [applyPanelWidth]);

  const endResize = useCallback((e) => {
    if (!resizeRef.current.active) return;
    resizeRef.current.active = false;
    if (resizeRef.current.raf) {
      cancelAnimationFrame(resizeRef.current.raf);
      resizeRef.current.raf = null;
    }
    document.body.style.userSelect = '';
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    setPanelWidth((w) => {
      const clamped = clampPanelWidth(w);
      persistPanelWidth(clamped);
      setPanelStop(nearestStop(clamped));
      return clamped;
    });
  }, []);

  const resetPanelWidth = useCallback(() => {
    applyPanelStop('default');
  }, [applyPanelStop]);

  const onResizeKeyDown = useCallback((e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    setPanelWidth((w) => {
      const delta = e.key === 'ArrowLeft' ? 32 : -32;
      const next = clampPanelWidth(w + delta);
      persistPanelWidth(next);
      return next;
    });
  }, []);

  // Dynamic bottom padding based on input bar height
  useEffect(() => {
    const el = inputBarRef.current;
    const content = contentRef.current;
    if (!el || !content) return;

    const ro = new ResizeObserver(() => {
      const h = el.getBoundingClientRect().height;
      content.style.paddingBottom = `calc(${h}px + env(safe-area-inset-bottom))`;
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [contentRef]);

  return (
    <motion.div
      initial={false}
      layout={false}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="fixed top-16 right-0 bottom-0 max-w-full bg-white z-50 shadow-2xl border-l flex flex-col neo-chat-panel"
      style={{
        width: panelWidth,
        maxHeight: 'calc(100dvh - 4rem)',
        userSelect: 'text',
        WebkitUserSelect: 'text',
      }}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize chat panel"
        tabIndex={0}
        onPointerDown={startResize}
        onPointerMove={onResizePointerMove}
        onPointerUp={endResize}
        onPointerCancel={endResize}
        onDoubleClick={resetPanelWidth}
        onKeyDown={onResizeKeyDown}
        className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize
                   hover:bg-[#00C2C2]/30 active:bg-[#00C2C2]/50 transition-colors z-10"
      />
      <div className="p-4 border-b bg-[#0A1A2F] text-white">
        <div className="flex justify-between items-center gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Zap className="w-5 h-5 text-[#00C2C2] shrink-0" />
            <h3 className="font-semibold truncate">
              {t('neoChatAssistantTitle')}
            </h3>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={shrinkPanel}
              disabled={atMinStop}
              title="Narrower"
              aria-label="Make chat narrower"
              className="text-gray-400 hover:text-white h-8 w-8 disabled:opacity-40"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={expandPanel}
              disabled={atMaxStop}
              title="Wider"
              aria-label="Make chat wider"
              className="text-gray-400 hover:text-white h-8 w-8 disabled:opacity-40"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              title="Minimise"
              aria-label="Minimise chat"
              className="text-gray-400 hover:text-white h-8 w-8"
            >
              <Minus className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleNewChat}
              className="text-gray-300 hover:text-white h-8 px-2 text-xs"
            >
              {t('neoChatNewChat')}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-gray-400 hover:text-white h-8 w-8"
              title="Close"
              aria-label="Close chat"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {clarification?.type !== 'meeting_prep' ? (
          <ChatAnchorChip account={selectedAccount} onClear={onClearAnchor} t={t} />
        ) : null}
      </div>

      <ScrollArea className="flex-1 p-4" ref={desktopScrollRef} style={{ userSelect: 'text', WebkitUserSelect: 'text' }}>
        <div
          ref={contentRef}
          className="space-y-4 pr-3"
          onMouseDownCapture={(e) => {
            const target = e.target;
            if (!(target instanceof Element)) return;
            if (target.closest('button, a, input, textarea, [role="textbox"], [data-cmdk-root], [data-radix-popper-content]')) return;
            if (!typingRef.current) focusInputIfNeeded();
          }}
          onClick={onChatDeepLinkClick}
          style={{ userSelect: 'text', WebkitUserSelect: 'text' }}
        >
          {isLoadingData ? ( // Conditional rendering for loading state
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#00C2C2] mb-4" />
              <p className="text-sm text-gray-500">Loading accounts and people...</p>
            </div>
          ) : (
            <>
              {messages.map((msg, index) => {
                const isLastBotMessage = msg.sender === 'bot' && index === messages.length - 1;
                const isOutreachMessage = msg.isOutreachMessage;

                return (
                  <div
                    key={msg.id || msg.text}
                    data-chat-message-id={msg.id}
                    className={`flex items-start gap-3 ${msg.sender === 'user' ? 'justify-end' : ''}`}
                    style={{ userSelect: 'text', WebkitUserSelect: 'text' }}
                  >
                    {msg.sender !== 'user' && (
                      <div className="w-8 h-8 rounded-full bg-[#00C2C2] flex items-center justify-center flex-shrink-0">
                        <Bot className="w-5 h-5 text-white" />
                      </div>
                    )}
                    <div
                      className={`p-3 rounded-lg break-words hyphens-auto ${
                        msg.sender === 'user' ? 'max-w-[85%]' : 'flex-1 min-w-0'
                      } ${msg.sender === 'bot' ? 'bg-gray-100 text-[#0A1A2F]' : msg.sender === 'user' ? 'bg-[#0A1A2F] text-white' : 'bg-blue-100 border border-blue-200 text-blue-900'}`}
                      style={{ userSelect: 'text', WebkitUserSelect: 'text', cursor: 'text' }}
                    >
                      {msg.type === 'morning_brief' ? (
                        <ChatMorningBriefMessage
                          message={msg}
                          onChipClick={onProactiveChipClick}
                          disabled={isSending}
                          eventTracking={eventTracking}
                          showChips={showProactiveChips}
                        />
                      ) : msg.type === 'trigger_nudges' ? (
                        <ChatTriggerNudgesMessage
                          message={msg}
                          onChipClick={onProactiveChipClick}
                          disabled={isSending}
                          showChips={showProactiveChips}
                        />
                      ) : msg.type === 'next_best_move' ? (
                        <NextBestMoveMessage
                          nbm={msg.nbm}
                          accountId={msg.accountId ?? accountPageId}
                          people={people}
                          eventTracking={eventTracking}
                        />
                      ) : (
                        <>
                      <ChatAnswerWithBlocks
                        text={msg.text}
                        blocks={msg.sender === 'user' ? null : msg.blocks}
                        sender={msg.sender}
                        linkRegistry={msg.linkRegistry}
                        people={people}
                      />
                      {msg.sender === 'user' ? null : (
                      <ChatBotMessageExtras
                        message={msg}
                        onCandidateSelect={onDisambiguationSelect}
                        onSuggestedNextClick={onSuggestedNextClick}
                        disabled={isSending}
                      />
                      )}
                      <ChatBotAnswerFeedback
                        message={msg}
                        tenantId={tenantId}
                        userId={currentUser?.id}
                        sessionId={chatSessionId}
                        headers={commonHeaders}
                        disabled={isSending}
                      />
                      {msg.isPlaybook && (
                        <Button
                            onClick={() => {
                                const userMessageText = `Execute playbook: "${msg.metadata.playbook_name}"`;
                                setMessages(prev => [...prev, { sender: 'user', text: userMessageText, id: nextMessageId() }]);
                                handleExecutePlaybook(msg.metadata);
                            }}
                            className="mt-3 w-full bg-[#00C2C2] hover:bg-[#00A3A3]"
                            size="sm"
                        >
                            <Zap className="w-4 h-4 mr-2" />
                            Execute Playbook
                        </Button>
                      )}
                      {isLastBotMessage && isOutreachMessage && lastOutreachContext && (
                        <Button
                            onClick={() => {
                                setClarification({
                                    type: 'configure_outreach',
                                    account: lastOutreachContext.account,
                                    persona: lastOutreachContext.persona
                                });
                                if (!typingRef.current) scrollToBottom(true);
                                focusInputIfNeeded();
                            }}
                            variant="outline"
                            className="mt-3 w-full border-[#00C2C2] text-[#00C2C2] hover:bg-[#00C2C2]/5"
                            size="sm"
                        >
                            Generate Another Message
                        </Button>
                      )}
                        </>
                      )}
                    </div>
                    {msg.sender === 'user' && (
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {currentUser?.profile_photo_url ? (
                            <img src={currentUser.profile_photo_url} alt="You" className="w-full h-full object-cover" />
                        ) : (
                            <UserIcon className="w-5 h-5 text-gray-600" />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {isSending && !messages.some((m) => m.isStreaming) && (
                <ChatPendingBubble progress={meetingPrepProgress} botClassName="w-5 h-5 text-white" />
              )}

              {clarification?.type === 'account' && (
                <div className="p-3" tabIndex={-1}>
                  <p className="text-sm font-medium mb-2 text-center text-[#0A1A2F]">Which account would you like to use?</p>
                  <Command className="rounded-lg border shadow-md">
                    <CommandInput placeholder={t('neoChatSearchAccounts')} />
                    <CommandList>
                      {isLoadingData ? ( // Use isLoadingData here
                        <CommandEmpty>
                          <div className="space-y-2 p-4">
                            <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
                            <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
                            <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
                          </div>
                        </CommandEmpty>
                      ) : accounts.length === 0 ? (
                        <CommandEmpty>No accounts found.</CommandEmpty>
                      ) : (
                        <CommandGroup>
                          {accounts.map((account) => (
                            <CommandItem key={account.id} onSelect={() => handleAccountSelect(account, clarification?.originalPrompt)} className="flex items-center gap-2">
                              {account.logo_url ? (
                                <img src={account.logo_url} alt={account.account_name} className="w-5 h-5 object-contain" />
                              ) : (
                                <Building2 className="w-5 h-5 text-gray-400" />
                              )}
                              <span>{account.account_name}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </div>
              )}

              {clarification?.type === 'persona' && (
                <div className="p-3" tabIndex={-1}>
                  <p className="text-sm font-medium mb-2 text-center text-[#0A1A2F]">
                    Which person at {clarification.account.account_name}?
                  </p>
                   <Button
                     variant="ghost" // Updated variant
                     size="sm"
                     onClick={() => {
                       // This should go back to the general outreach selection (OutreachConfigPicker)
                       setClarification({ type: 'select_outreach_target', preselectedAccount: null, preselectedPersona: null }); // Clear all and go to picker
                       if (!typingRef.current) scrollToBottom(true);
                       focusInputIfNeeded();
                     }}
                     className="text-[#00C2C2] hover:text-[#00A3A3] hover:bg-[#00C2C2]/10 px-2 h-8" // Updated classes
                   >
                    <ChevronLeft className="w-4 h-4 mr-1" /> {/* Added icon */}
                    Back to people selection
                  </Button>
                  <Command className="rounded-lg border shadow-md mt-2">
                    <CommandInput placeholder={t('neoChatSearchPeople')} />
                    <CommandList>
                      <CommandEmpty>
                        {isLoadingData ? ( // Use isLoadingData here
                          <p>Loading people...</p>
                        ) : (
                          <p>No people found for this account.</p>
                        )}
                      </CommandEmpty>
                      <CommandGroup>
                        {people.filter((p) => personaBelongsToAccount(p, clarification.account)).map((person) => (
                          <CommandItem key={person.id} onSelect={() => handlePersonaSelect(person)} className="flex items-center gap-2">
                            <PersonAvatar
                              personId={person.person_id || person.id}
                              hostedPhotoUrl={person.hosted_photo_url}
                              linkedinPhotoUrl={person.profile_photo_url}
                              firstName={person.first_name}
                              lastName={person.last_name}
                              size="xs"
                            />
                            <div className="flex flex-col">
                              <span>{person.first_name} {person.last_name}</span>
                              {person.role_title && (
                                <span className="text-xs text-gray-500">{person.role_title}</span>
                              )}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </div>
              )}
              {clarification?.type === 'select_outreach_target' && (
                <OutreachConfigPicker
                    accounts={accounts}
                    people={people}
                    isLoadingData={isLoadingData}
                    preselectedAccount={clarification.preselectedAccount}
                    preselectedPersona={clarification.preselectedPersona}
                    onSelect={(selectedAccount, selectedPersona) => {
                        setClarification((prev) => ({
                            type: 'configure_outreach',
                            account: selectedAccount,
                            persona: selectedPersona,
                            prefilledOutreachContext: prev?.prefilledOutreachContext,
                        }));
                        if (!typingRef.current) scrollToBottom(true);
                        focusInputIfNeeded();
                    }}
                    onCancel={() => {
                        setClarification(null);
                        if (!typingRef.current) scrollToBottom(true);
                        focusInputIfNeeded();
                    }}
                />
              )}

              {clarification?.type === 'meeting_prep' && (
                <MeetingPrepFlow
                  accounts={accounts}
                  people={people}
                  isLoadingData={isLoadingData}
                  tenantId={tenantId}
                  initialAccount={clarification?.initialAccount}
                  onGenerate={async ({ account, meetingType, attendeeIds, product, category }) => {
                    setClarification(null);
                    setIsSending(true);
                    setMeetingPrepProgress(null);
                    
                    const userMessage = {
                      sender: 'user',
                      text: t('neoChatPrepareMeetingUser', {
                        meetingType: getMeetingTypeLabel(t, meetingType),
                        accountName: account.account_name,
                      }),
                      id: nextMessageId()
                    };
                    setMessages(prev => [...prev, userMessage]);
                    
                    try {
                      if (!product) {
                        console.warn('⚠️ Meeting prep product could not be inferred from account', {
                          tenantId,
                          accountName: account?.account_name,
                          accountId: account?.account_id || account?.id
                        });
                      }

                      console.log('🎯 Calling meeting-prep API:', {
                        url: `${API_BASE_URL}/api/meeting-prep`,
                        account: account.account_name,
                        meetingType,
                        tenantId,
                        product,
                        category,
                        language: resolvePlatformUiLanguage(currentUser, tenantId)
                      });

                      const data = await requestMeetingPrep({
                        apiBase: API_BASE_URL,
                        headers: {
                          'Content-Type': 'application/json',
                          ...commonHeaders,
                        },
                        body: {
                          account_id: account.account_id || account.id,
                          account_name: account.account_name,
                          meeting_type: meetingType,
                          attendee_ids: attendeeIds,
                          tenant_id: tenantId,
                          product,
                          product_context: product,
                          category,
                          language: resolvePlatformUiLanguage(currentUser, tenantId),
                          ...(clarification?.salesMapPayload
                            ? { sales_map: clarification.salesMapPayload }
                            : {}),
                        },
                        onProgress: (progress) => {
                          setMeetingPrepProgress(progress);
                          if (!typingRef.current) scrollToBottom(true);
                        },
                      });
                      
                      const botMessage = buildMeetingPrepBotMessage(data);
                      setMessages(prev => [...prev, botMessage]);
                      
                      logNeoRecommendation({
                        tenantId: getCurrentTenantId(currentUser, tenantId),
                        userId: currentUser?.id || getClientTraceId(),
                        accountId: extractAccountId(account),
                        accountName: account?.account_name,
                        recommendationType: 'meeting_prep',
                        source: 'chatbot',
                        metadata: {
                          meeting_type: meetingType,
                          attendees_count: attendeeIds.length
                        }
                      }).catch(err => console.error('Analytics error:', err));
                      
                    } catch (error) {
                      console.error('Error generating meeting prep:', error);
                      
                      let errorText = error.message;
                      if (error.name === 'AbortError') {
                        errorText = t('neoChatErrorMeetingPrepTimeout');
                      }
                      
                      const errorMessage = {
                        sender: 'bot',
                        text: t('neoChatErrorMeetingPrep', { error: errorText }),
                        id: nextMessageId()
                      };
                      setMessages(prev => [...prev, errorMessage]);
                    } finally {
                      setMeetingPrepProgress(null);
                      setIsSending(false);
                    }
                  }}
                  onCancel={() => {
                    setClarification(null);
                    if (!typingRef.current) scrollToBottom(true);
                    focusInputIfNeeded();
                  }}
                />
              )}

              {clarification?.type === 'configure_outreach' && (
                <OutreachConfigurationForm
                  account={clarification.account}
                  persona={clarification.persona}
                  handleSend={handleSend}
                  onBack={() => {
                      setClarification({
                        type: 'select_outreach_target',
                        preselectedAccount: null,
                        preselectedPersona: null
                      });
                      if (!typingRef.current) scrollToBottom(true);
                      focusInputIfNeeded();
                  }}
                  isLoading={isLoading}
                  isSending={isSending}
                  typingRef={typingRef}
                  scrollToBottom={scrollToBottom}
                  focusInputIfNeeded={focusInputIfNeeded}
                  currentUser={currentUser}
                  initialUserContext={clarification.prefilledOutreachContext}
                />
              )}
            </>
          )}

          <div ref={bottomRef} />
        </div>
      </ScrollArea>

      <div ref={inputBarRef} className="p-4 border-t bg-white">
        {/* Input bar is only visible when not in a picker/config state */}
        {!(clarification?.type === 'select_outreach_target' || clarification?.type === 'configure_outreach' || clarification?.type === 'account' || clarification?.type === 'persona' || clarification?.type === 'meeting_prep') ? (
          <>
            <div className="flex flex-wrap gap-2 mb-2">
                {suggestionChips.map(chip => (
                    <Button key={chip.label} variant="outline" size="sm" className="text-xs" onClick={() => handleChipClick(chip.prompt, chip.requires)}>
                        {chip.label}
                    </Button>
                ))}
            </div>
            <div className="flex items-center gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    if (composingRef.current) return;
                    e.preventDefault();
                    handleSend();
                  }
                }}
                onCompositionStart={() => { composingRef.current = true; }}
                onCompositionEnd={() => { composingRef.current = false; }}
                onFocus={() => { typingRef.current = true; }}
                onBlur={() => { typingRef.current = false; }}
                placeholder={t('neoChatInputPlaceholder')}
                disabled={isSending}
              />
              <Button onClick={() => handleSend()} disabled={isSending} className="bg-[#00C2C2] hover:bg-[#00A3A3]">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </>
        ) : null }
      </div>
    </motion.div>
  );
});

export default function NeoChatPanel({ context, onClose, currentTenant, currentUser: currentUserProp, onBriefViewed, pageAccount }) {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useTranslation();
  const suggestionChips = useMemo(() => getSuggestionChips(t), [t]);
  const briefOnAccountTemplate = useMemo(() => t('neoChatBriefOnAccountName'), [t]);
  const tenderPrompt = useMemo(() => t('neoChatPromptShowTenders'), [t]);

  const [clientTraceId] = useState(getClientTraceId);
  const [chatSessionId, setChatSessionId] = useState(null);
  const [proactiveFetchNonce, setProactiveFetchNonce] = useState(0);
  const [adminTenantTick, setAdminTenantTick] = useState(0);
  const prevTenantRef = useRef(null);
  const skipSessionRestoreRef = useRef(false);
  const autoSendKeyRef = useRef(null);
  const pendingAutoSendRef = useRef(null);
  const nbmViewActiveRef = useRef(false);

  const [currentUser, setCurrentUser] = useState(currentUserProp || null);
  const [messages, setMessages] = useState(() => {
    if (context?.autoExecutePlaybook) {
      console.log('🎯 Playbook execution detected, starting fresh chat');
      return [];
    }
    return [createDefaultGreetingMessage(language)];
  });
  const [conversationHydrated, setConversationHydrated] = useState(false);

  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // Renamed from isFetchingInitialData and added loadingRef
  const [isLoadingData, setIsLoadingData] = useState(false);
  const loadingRef = useRef(null);

  const [accounts, setAccounts] = useState([]); // Remove cache reading from initial state

  const [people, setPeople] = useState([]); // Remove cache reading from initial state

  // Clarification can now be 'account', 'persona', 'select_outreach_target', or 'configure_outreach'
  const [clarification, setClarification] = useState(null);
  const [userPreferences, setUserPreferences] = useState(null);
  const [playbookExecuted, setPlaybookExecuted] = useState(false);
  const [lastOutreachContext, setLastOutreachContext] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [anchorDismissed, setAnchorDismissed] = useState(false);
  const lastPageAnchorRef = useRef(null);

  // Separate refs for desktop and mobile
  const desktopScrollRef = useRef(null);
  const mobileScrollRef = useRef(null);
  const bottomRef = useRef(null);
  const contentRef = useRef(null);
  const inputRef = useRef(null);
  const typingRef = useRef(false);
  const composingRef = useRef(false);

  // 👇 NEW: resolve tenant like Today's Plan (admin-gated)
  const [tenantId, setTenantId] = useState(() =>
    resolveChatPanelTenant(null, currentTenant || context?.tenant_id, null),
  );

  const platformLanguage = useMemo(
    () => resolvePlatformUiLanguage(currentUser, tenantId),
    [currentUser, tenantId],
  );

  useEffect(() => {
    const onAdminTenantChange = () => setAdminTenantTick((n) => n + 1);
    window.addEventListener(ADMIN_TENANT_CHANGE_EVENT, onAdminTenantChange);
    return () => window.removeEventListener(ADMIN_TENANT_CHANGE_EVENT, onAdminTenantChange);
  }, []);

  // Sync tenant when context/user changes — reset chatbot state on switch
  useEffect(() => {
    const adminTenant =
      currentUser?.role === 'admin'
        ? (currentTenant || localStorage.getItem('admin-active-tenant'))
        : null;
    const newTenant = resolveChatPanelTenant(
      adminTenant,
      currentTenant || context?.tenant_id,
      currentUser,
    );

    if (!newTenant) return;

    if (newTenant === tenantId) {
      if (prevTenantRef.current == null) prevTenantRef.current = newTenant;
      return;
    }

    const isTenantSwitch =
      prevTenantRef.current != null && prevTenantRef.current !== newTenant;

    console.log('🔄 NeoChatPanel: Tenant changed:', tenantId, '→', newTenant, {
      adminTenant,
      contextTenant: context?.tenant_id,
      userTenant: currentUser?.tenant_id,
      isTenantSwitch,
    });

    handleChatbotTenantSwitch({
      previousTenantId: tenantId,
      nextTenantId: newTenant,
      userId: currentUser?.id,
    });

    prevTenantRef.current = newTenant;
    setTenantId(newTenant);
    setProactiveFetchNonce((n) => n + 1);

    if (isTenantSwitch) {
      skipSessionRestoreRef.current = true;
      setChatSessionId(null);
    } else if (currentUser?.id) {
      setChatSessionId(loadStoredChatSessionId(newTenant, currentUser.id));
    }

    if (!context?.autoExecutePlaybook) {
      // Fresh start on every tenant switch (option a): never hydrate B's saved
      // conversation when arriving from A — proactive brief/triggers refetch separately.
      setMessages([createDefaultGreetingMessage(language)]);
    }
    setConversationHydrated(true);
    setAccounts([]);
    setPeople([]);
    setClarification(null);
    setSelectedAccount(null);
    setAnchorDismissed(false);
  }, [
    context?.tenant_id,
    currentUser?.tenant_id,
    currentUser?.id,
    currentUser?.role,
    currentTenant,
    tenantId,
    context?.autoExecutePlaybook,
    adminTenantTick,
    language,
  ]);

  useEffect(() => {
    clearLegacyConversationStorage();
  }, []);

  const resolvedUserId = currentUserProp?.id || currentUser?.id;

  useEffect(() => {
    if (conversationHydrated || context?.autoExecutePlaybook || context?.autoSendChatbot || context?.showNextBestMove) return;
    if (!tenantId || !resolvedUserId) return;

    const stored = loadConversationMessages(tenantId, resolvedUserId);
    if (stored?.length) {
      setMessages(normalizeMsgs(stored));
    }
    setConversationHydrated(true);
  }, [
    tenantId,
    resolvedUserId,
    conversationHydrated,
    context?.autoExecutePlaybook,
  ]);

  useEffect(() => {
    if (currentUserProp?.id) {
      setCurrentUser((prev) => (prev?.id === currentUserProp.id ? prev : currentUserProp));
    }
  }, [currentUserProp]);

  useEffect(() => {
    if (
      context?.autoExecutePlaybook ||
      context?.directToOutreach ||
      context?.autoSendChatbot ||
      context?.showNextBestMove ||
      context?.triggerMeetingPrep
    ) {
      return;
    }
    if (!tenantId || !resolvedUserId) return;

    let cancelled = false;

    (async () => {
      try {
        const [briefData, triggerData] = await Promise.all([
          fetchChatbotBriefCached(tenantId, resolvedUserId, { language: platformLanguage }),
          fetchChatbotTriggersCached(tenantId, resolvedUserId, { language: platformLanguage }),
        ]);
        if (cancelled) return;

        const briefItems = filterBriefResponseItems(
          Array.isArray(briefData?.items) ? briefData.items : [],
          tenantId,
        );
        const briefPayload =
          briefItems.length > 0 ? { ...briefData, items: briefItems } : null;

        if (briefPayload?.items?.length) {
          markChatbotBriefViewed(tenantId, resolvedUserId);
        }
        markChatbotTriggersViewed(tenantId, resolvedUserId);

        const briefMsg = briefPayload?.items?.length
          ? buildMorningBriefMessage(briefPayload, tenantId, platformLanguage)
          : null;
        const triggerRawItems = Array.isArray(triggerData?.items)
          ? triggerData.items
          : [];

        if (!briefMsg && triggerRawItems.length === 0) return;

        onBriefViewed?.();

        setMessages((prev) => {
          const scopedPrev = stripForeignProactiveMessages(prev, tenantId);
          let next = scopedPrev.filter((m) => !isProactiveChatMessage(m));

          if (
            briefMsg &&
            !next.some((m) => m.sender === 'user')
          ) {
            next = [briefMsg];
          } else if (briefMsg) {
            next = [briefMsg, ...next];
          }

          if (triggerRawItems.length > 0) {
            const existingTriggerIds = new Set();
            next.forEach((m) => {
              if (m.type === 'trigger_nudges' && Array.isArray(m.triggerItems)) {
                m.triggerItems.forEach((t) => {
                  if (t?.id) existingTriggerIds.add(String(t.id));
                });
              }
            });
            const newTriggerRaw = triggerRawItems.filter((t) => {
              const id = t?.id != null ? String(t.id) : null;
              return id && !existingTriggerIds.has(id);
            });
            const triggerMsg = buildTriggerNudgesMessage(newTriggerRaw, tenantId, platformLanguage);
            if (triggerMsg) {
              const hasBrief = next.some((m) => m.type === 'morning_brief');
              const onlyDefaultGreeting =
                next.length === 1 && isDefaultGreetingMessage(next[0]);
              if (!hasBrief && onlyDefaultGreeting) {
                next = [triggerMsg];
              } else {
                next = [...next, triggerMsg];
              }
            }
          }

          return next;
        });
      } catch {
        // Proactive content is opportunistic — fail silently
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    tenantId,
    resolvedUserId,
    proactiveFetchNonce,
    platformLanguage,
    context?.autoExecutePlaybook,
    context?.directToOutreach,
    onBriefViewed,
  ]);

  const commonHeaders = useMemo(
    () => ({
      'x-tenant-id': tenantId,
      'x-user-id': currentUser?.id || '',
    }),
    [tenantId, currentUser?.id],
  );

  const accountPageRouteId = useMemo(() => {
    if (!location.pathname.includes('AccountFinal')) return null;
    const id = new URLSearchParams(location.search).get('id');
    return id?.trim() || null;
  }, [location.pathname, location.search]);

  const chatbotAskAccountId = useMemo(() => {
    const ctxTenant = context?.tenant_id;
    if (ctxTenant && tenantId && ctxTenant !== tenantId) return null;

    return resolveChatbotAccountId(context, {
      routeRecordId: accountPageRouteId,
      accounts,
      pageAccount,
    }) || null;
  }, [context, tenantId, accountPageRouteId, accounts, pageAccount]);

  // Sales Map / NBM still need a page id before the business key is resolved.
  const accountPageId = chatbotAskAccountId || accountPageRouteId;

  useEffect(() => {
    setAnchorDismissed(false);
  }, [chatbotAskAccountId, accountPageRouteId]);

  useEffect(() => {
    if (anchorDismissed || !chatbotAskAccountId) return;
    const name =
      lookupAccountName(chatbotAskAccountId, accounts, context)
      || lookupAccountName(accountPageRouteId, accounts, context)
      || pageAccount?.account_name
      || '';
    const pageChanged = lastPageAnchorRef.current !== chatbotAskAccountId;
    lastPageAnchorRef.current = chatbotAskAccountId;
    setSelectedAccount((prev) => {
      if (
        !pageChanged
        && prev?.account_id
        && prev.account_id !== chatbotAskAccountId
        && !isAccountRowUuid(prev.account_id)
      ) {
        return prev;
      }
      if (prev?.account_id === chatbotAskAccountId && (prev.account_name || !name)) return prev;
      return { account_id: chatbotAskAccountId, account_name: name || prev?.account_name || '' };
    });
  }, [chatbotAskAccountId, accountPageRouteId, accounts, context, pageAccount, anchorDismissed]);

  const onClearAnchor = useCallback(() => {
    setAnchorDismissed(true);
    setSelectedAccount(null);
  }, []);

  const applyResolvedAccount = useCallback((resolved) => {
    const next = accountFromResolvedAccount(resolved);
    if (!next) return;
    setAnchorDismissed(false);
    setSelectedAccount((prev) => ({
      account_id: next.account_id || prev?.account_id || null,
      account_name: next.account_name || prev?.account_name || '',
    }));
  }, []);

  useEffect(() => {
    if (!context) {
      nbmViewActiveRef.current = false;
    }
  }, [context]);

  useEffect(() => {
    if (!accountPageId || !tenantId || !resolvedUserId) return;

    const shouldSyncNbm = nbmViewActiveRef.current || context?.showNextBestMove;
    if (!shouldSyncNbm) return;

    let cancelled = false;

    (async () => {
      try {
        const payload = await fetchAccountSalesMap(
          tenantId,
          accountPageId,
          resolvedUserId,
          { force: true },
        );
        if (cancelled) return;

        nbmViewActiveRef.current = true;
        const nbm = sanitizeNextBestMoveForDisplay(resolveNextBestMove(payload));
        const nbmMsg = nbm?.move
          ? {
              sender: 'bot',
              type: 'next_best_move',
              nbm,
              accountId: accountPageId,
              id: `nbm-${accountPageId}`,
            }
          : null;

        setMessages((prev) => {
          if (!nbmMsg) return prev.filter((m) => m.type !== 'next_best_move');
          const hasUser = prev.some((m) => m.sender === 'user');
          if (!hasUser) return [nbmMsg];
          if (prev.some((m) => m.type === 'next_best_move')) {
            return prev.map((m) => (m.type === 'next_best_move' ? nbmMsg : m));
          }
          return [nbmMsg, ...prev];
        });
      } catch {
        // Keep any optimistic card if fetch fails
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    accountPageId,
    tenantId,
    resolvedUserId,
    context?.showNextBestMove,
    context?.chatbotRequestId,
  ]);

  const visibleMessages = useMemo(
    () => stripForeignProactiveMessages(messages, tenantId),
    [messages, tenantId],
  );

  useEffect(() => {
    const hasForeignProactive = messages.some(
      (m) => isProactiveChatMessage(m) && !proactiveMessageMatchesTenant(m, tenantId),
    );
    if (hasForeignProactive) {
      setProactiveFetchNonce((n) => n + 1);
    }
  }, [messages, tenantId]);

  console.log('🏢 NeoChatPanel tenant context:', {
    tenantId,
    userId: currentUser?.id,
    chatSessionId,
    accountPageId,
  });

  // Define focusInputIfNeeded function
  const focusInputIfNeeded = useCallback(() => {
    // Focus the input on desktop, but be careful on mobile
    if (inputRef.current) {
      // On mobile, only focus if not in a picker/modal state (i.e., no clarification)
      if (isDesktop || !clarification) {
        try {
          inputRef.current.focus();
        } catch (e) {
          // Silently handle focus errors (can happen during transitions)
        }
      }
    }
  }, [isDesktop, clarification]);

  // Helper to get the active viewport element
  const getViewport = useCallback(() => {
    const root = (isDesktop ? desktopScrollRef.current : mobileScrollRef.current);
    return root?.querySelector('[data-radix-scroll-area-viewport]');
  }, [isDesktop]);

  // Robust scroll-to-bottom utility
  const scrollToBottom = useCallback((force = false) => {
    const viewport = getViewport();
    if (!viewport) return;

    // If user is typing, do not scroll (even if force=true)
    if (typingRef.current) return;

    // Only scroll if near bottom, unless we're opening a pane (handled by callers)
    const nearBottom = viewport.scrollTop >= (viewport.scrollHeight - viewport.clientHeight - 50);
    if (!force && !nearBottom) return;

    // Use requestAnimationFrame for smoother scrolling and to ensure DOM is updated
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        bottomRef.current?.scrollIntoView({ block: 'end', inline: 'nearest', behavior: 'auto' });
      });
    });
  }, [getViewport, bottomRef, typingRef]);

  // Lock body scroll while chat is open
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    const isIOS = /iP(ad|hone|od)/.test(navigator.userAgent);

    if (!isIOS) {
      // avoid layout shift when scrollbar disappears
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, []);

  // Persist conversation per tenant + user (never store proactive brief/triggers)
  useEffect(() => {
    if (!context?.autoExecutePlaybook && tenantId && resolvedUserId) {
      const persistable = messages.filter((m) => !isProactiveChatMessage(m));
      saveConversationMessages(tenantId, resolvedUserId, persistable);
    }
  }, [messages, context?.autoExecutePlaybook, tenantId, resolvedUserId]);

  // When messages change, scroll to bottom if near bottom
  useEffect(() => {
    scrollToBottom(false);
  }, [messages, scrollToBottom]);

  // Only react to clarification pane changes (not background loads)
  useEffect(() => {
    if (!typingRef.current) scrollToBottom(true);
  }, [clarification, scrollToBottom, typingRef]);

  // Force scroll to bottom when people load, as this can add significant content
  useEffect(() => {
    if (people?.length && !typingRef.current && (clarification?.type === 'persona' || clarification?.type === 'select_outreach_target')) {
      scrollToBottom(true);
    }
  }, [people, scrollToBottom, typingRef, clarification]);

  // Use ResizeObserver to detect content height changes and scroll if needed
  useEffect(() => {
    const viewport = getViewport();
    const node = contentRef.current;
    if (!viewport || !node) return;

    const ro = new ResizeObserver(() => {
      if (typingRef.current) return;
      scrollToBottom(Boolean(clarification));
    });

    ro.observe(node);
    return () => ro.disconnect();
  }, [clarification, isDesktop, getViewport, scrollToBottom, typingRef]);

  // Focus input when panel opens
  useEffect(() => {
    focusInputIfNeeded();
  }, [focusInputIfNeeded]);

  const loadData = useCallback(async () => {
    if (loadingRef.current === tenantId) {
      console.log('⏭️ NeoChatPanel: Load already in progress for:', tenantId);
      return;
    }
    loadingRef.current = tenantId;
    setIsLoadingData(true);

    try {
      // Use static import that's already at the top of the file
      const user = await fetchWithRetry(() => UserEntity.me(), 3, 500).catch((err) => {
        console.warn("Failed to fetch current user, using default:", err);
        return { tenant_id: tenantId, access_level: DEFAULT_ACCESS, id: clientTraceId, profile_photo_url: null };
      });
      setCurrentUser(user);
      if (user?.id && !skipSessionRestoreRef.current) {
        setChatSessionId(loadStoredChatSessionId(tenantId, user.id));
      }
      skipSessionRestoreRef.current = false;

      const effectiveTenant = tenantId;

      // ALWAYS load fresh data, ignore cache for now to diagnose
      console.log('🔄 NeoChatPanel: FORCING fresh data load (ignoring cache) for tenant:', effectiveTenant);
      const freshData = await loadChatData(effectiveTenant);
      setAccounts(freshData.accounts || []);
      setPeople(freshData.people || []);

      // Fetch user preferences with better error handling
      try {
        const { AlertPreference } = await import('@/api/entities');
        const userIdForPrefs = user?.id || clientTraceId;

        // Check if AlertPreference has the methods we need
        if (AlertPreference && typeof AlertPreference.list === 'function') {
          const allPreferences = await fetchWithRetry(() => AlertPreference.list());
          const userPrefs = allPreferences.find(p => p.user_id === userIdForPrefs);
          setUserPreferences(userPrefs || null);
        } else if (AlertPreference && typeof AlertPreference.filter === 'function') {
          const preferences = await fetchWithRetry(() => AlertPreference.filter({ user_id: userIdForPrefs }));
          setUserPreferences(preferences.length > 0 ? preferences[0] : null);
        } else {
          console.warn('AlertPreference entity does not have list() or filter() method');
          setUserPreferences(null);
        }
      } catch (prefError) {
        console.error("Failed to fetch user preferences:", prefError);
        setUserPreferences(null);
      }

    } catch (error) {
      console.error('❌ NeoChatPanel: Error loading chatbot data:', error);
      setAccounts([]);
      setPeople([]);
      setCurrentUser(prev => prev || { tenant_id: tenantId, access_level: DEFAULT_ACCESS, id: clientTraceId });
      setUserPreferences(null);
    } finally {
      setIsLoadingData(false);
      loadingRef.current = null;
      console.log('✅ loadData completed.');
    }
  }, [tenantId, clientTraceId, setAccounts, setPeople, setCurrentUser, setUserPreferences]);

  useEffect(() => {
    // Load data immediately when panel opens or tenant changes
    if (tenantId) {
      loadData();
    }
  }, [tenantId, loadData]);

  const handleNewChat = useCallback(() => {
    if (currentUser?.id) {
      clearStoredChatSessionId(tenantId, currentUser.id);
    }
    setChatSessionId(null);
    setClarification(null);
    setInput('');
    setMessages([
      createDefaultGreetingMessage(language),
    ]);
    if (currentUser?.id) {
      saveConversationMessages(tenantId, currentUser.id, [
        createDefaultGreetingMessage(language),
      ]);
    }
    setProactiveFetchNonce((n) => n + 1);
  }, [tenantId, currentUser?.id, language, setClarification, setInput, setMessages]);

  const sendChatbotMessage = useCallback(async (question, {
    addUserMessage = true,
    askContext,
    extraContext,
    sessionIdOverride,
    accountIdOverride,
  } = {}) => {
    const trimmed = String(question || '').trim();
    const userId = currentUser?.id || currentUserProp?.id;
    if (!trimmed || !tenantId || !userId) return null;

    const payloadContext = extraContext ?? askContext ?? null;

    if (addUserMessage) {
      setMessages((prev) => [...prev, { sender: 'user', text: trimmed, id: nextMessageId() }]);
    }
    setIsSending(true);

    const streamMessageId = nextMessageId();
    setMessages((prev) => [
      ...prev,
      {
        sender: 'bot',
        text: '',
        id: streamMessageId,
        isStreaming: true,
      },
    ]);

    try {
      const isLocal =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      const requestBody = buildChatbotAskBody({
        question: trimmed,
        tenantId,
        userId,
        sessionId: sessionIdOverride ?? chatSessionId,
        accountId: chatbotAskAccountIdFromSources({
          override: accountIdOverride,
          selectedAccountId: selectedAccount?.account_id,
          resolved: anchorDismissed ? null : chatbotAskAccountId,
        }),
        context: payloadContext,
        language: platformLanguage,
        debug: isLocal,
        stream: true,
      });

      const data = await consumeChatbotAskStream({
        apiBaseUrl: API_BASE_URL,
        body: requestBody,
        headers: {
          'Content-Type': 'application/json',
          ...commonHeaders,
        },
        credentials: 'include',
        onToken: (delta) => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === streamMessageId ? { ...m, text: `${m.text || ''}${delta}` } : m,
            ),
          );
        },
        onBlocks: (blocksData) => {
          const blocks = normalizeChatBlocks(blocksData?.blocks);
          if (!blocks) return;
          setMessages((prev) =>
            prev.map((m) => (m.id === streamMessageId ? { ...m, blocks } : m)),
          );
          scrollToBottom(false);
        },
        onDone: (doneData) => {
          if (doneData?.session_id) {
            setChatSessionId(doneData.session_id);
            if (currentUser?.id) {
              saveStoredChatSessionId(tenantId, currentUser.id, doneData.session_id);
            }
          }
          const finalMessage = buildBotMessageFromChatbotResponse(doneData);
          applyResolvedAccount(doneData?.resolved_account);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === streamMessageId
                ? {
                    ...finalMessage,
                    id: streamMessageId,
                    text: doneData?.answer ?? m.text,
                    blocks: finalMessage.blocks ?? m.blocks,
                    sourceQuestion: trimmed,
                    isStreaming: false,
                  }
                : m,
            ),
          );
        },
      });

      return data;
    } catch (error) {
      console.error('Error calling chatbot:', error);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === streamMessageId
            ? {
                sender: 'bot',
                text: t('neoChatErrorCallNeo', { error: error.message }),
                id: streamMessageId,
                isStreaming: false,
              }
            : m,
        ),
      );
      return null;
    } finally {
      setIsSending(false);
    }
  }, [
    selectedAccount?.account_id,
    chatbotAskAccountId,
    anchorDismissed,
    applyResolvedAccount,
    chatSessionId,
    commonHeaders,
    currentUser?.id,
    currentUserProp?.id,
    platformLanguage,
    setIsSending,
    setMessages,
    t,
    tenantId,
    scrollToBottom,
  ]);

  const onDisambiguationSelect = useCallback(
    (accountName) => {
      if (!accountName) return;
      sendChatbotMessage(`I meant ${accountName}`);
    },
    [sendChatbotMessage],
  );

  const onSuggestedNextClick = useCallback(
    async (item, parentMessage) => {
      const actionType = String(item?.actionType ?? item?.action_type ?? 'prefill_question').trim() || 'prefill_question';
      if (
        actionType !== 'prefill_question'
        && actionType !== 'navigate'
        && actionType !== 'compose_via_persona'
        && actionType !== 'add_account_to_workspace'
        && actionType !== 'request_account_from_admin'
      ) {
        return;
      }
      const personId = item?.targetPersonId ?? item?.target_person_id ?? null;
      const accountId = item?.targetAccountId ?? item?.target_account_id ?? null;
      const userId = currentUser?.id || currentUserProp?.id;
      const parentId = parentMessage?.id;
      const patchParent = (patch) => {
        if (parentId == null) return;
        setMessages((prev) => prev.map((m) => (m.id === parentId ? { ...m, ...patch } : m)));
      };

      if (actionType === 'compose_via_persona') {
        if (!personId) return;

        const pagePath = buildComposeViaPersonaPagePath({
          personId,
          accountId,
          composeBrief: item?.composeBrief ?? item?.compose_brief ?? '',
          autoGenerate: true,
        });
        if (!pagePath) return;

        onClose?.();
        navigate(createPageUrl(pagePath));
        return;
      }

      if (actionType === 'navigate') {
        let pagePath = null;
        if (personId) {
          pagePath = buildComposeViaPersonaPagePath({
            personId,
            accountId,
            autoGenerate: false,
          });
        } else if (accountId) {
          pagePath = `AccountFinal?id=${encodeURIComponent(String(accountId).trim())}`;
        }
        if (!pagePath) return;

        onClose?.();
        navigate(createPageUrl(pagePath));
        return;
      }

      if (isWorkspaceAccountChipAction(actionType)) {
        if (!accountId || !tenantId || !userId || isSending || parentMessage?.chipActionPending) return;
        patchParent({ chipActionPending: true, chipNote: null });
        const accountName =
          item?.targetAccountName
          || parentMessage?.accountNotProvisioned?.accountName
          || 'this account';
        const originalQuestion = String(parentMessage?.sourceQuestion || '').trim();

        if (actionType === 'add_account_to_workspace') {
          try {
            await addMarketAccount(tenantId, { account_id: accountId });
            patchParent({
              chipActionPending: false,
              suggestedNext: [],
              chipNote: "Added — ask me again and I'll answer from the full record",
            });
            if (originalQuestion) {
              await sendChatbotMessage(originalQuestion, { addUserMessage: false });
            }
          } catch (e) {
            const cap = isPilotCapReachedError(e);
            const note = pilotErrorMessage(e, 'Could not add this account.');
            patchParent({
              chipActionPending: false,
              chipNote: note,
              ...(cap
                ? {
                    suggestedNext: [
                      requestChipFromNotProvisioned(
                        { ...parentMessage?.accountNotProvisioned, kind: 'at_cap' },
                        accountId,
                        accountName,
                      ),
                    ],
                    accountNotProvisioned: {
                      ...(parentMessage?.accountNotProvisioned || {}),
                      kind: 'at_cap',
                      poolAccountId: accountId,
                      accountName,
                    },
                  }
                : {}),
            });
          }
          return;
        }

        try {
          const kind = parentMessage?.accountNotProvisioned?.kind;
          const reasonCode = kind === 'at_cap' || kind === 'needs_admin' ? kind : 'needs_admin';
          const data = await postChatbotAccountRequest({
            apiBaseUrl: API_BASE_URL,
            headers: commonHeaders,
            payload: {
              tenant_id: tenantId,
              user_id: userId,
              pool_account_id: accountId,
              account_name: accountName,
              reason_code: reasonCode,
              question: originalQuestion || undefined,
              session_id: chatSessionId || undefined,
            },
          });
          const note = data?.message
            || (data?.already_open ? 'This request is already queued.' : 'Request sent to NEO.');
          patchParent({
            chipActionPending: false,
            chipNote: note,
          });
        } catch (e) {
          patchParent({
            chipActionPending: false,
            chipNote: e?.message || 'Could not send the request.',
          });
        }
        return;
      }

      const prompt = String(item?.prompt ?? item?.label ?? '').trim();
      if (!prompt || isSending) return;
      setMessages((prev) => [...prev, { sender: 'user', text: prompt, id: nextMessageId() }]);
      sendChatbotMessage(prompt, { addUserMessage: false });
    },
    [
      isSending,
      sendChatbotMessage,
      setMessages,
      navigate,
      onClose,
      tenantId,
      currentUser?.id,
      currentUserProp?.id,
      commonHeaders,
      chatSessionId,
    ],
  );

  const handleExecutePlaybook = useCallback(async (playbookContext) => {
    console.log('🚀 Starting playbook execution for:', playbookContext);
    setIsSending(true);

    try {
        if (!playbookContext) {
            throw new Error('No playbook context provided');
        }

        const prompt = `Generate a strategic execution plan for ${playbookContext.account_name} using the "${playbookContext.playbook_name}" playbook.

**Playbook Goal:** ${playbookContext.goal || 'Not specified'}

**Actions to include:** ${playbookContext.actions || 'Not specified'}

Please provide a comprehensive, actionable strategy that includes:
1. Next steps to take
2. Specific messaging recommendations
3. Timeline suggestions
4. Success metrics to track

Format your response clearly and professionally.`;

        if (!prompt || prompt.length === 0) {
            console.error('Prompt is empty!');
            throw new Error('Failed to generate prompt');
        }

        console.log('[DEBUG] Generated prompt:', prompt.substring(0, 100) + '...');

        const requestBody = {
            question: prompt,
            tenant_id: tenantId,
            access_level: currentUser?.access_level || DEFAULT_ACCESS,
            session_id: clientTraceId,
            user_id: currentUser?.id || clientTraceId
        };

        console.log('📤 Sending /api/answer request:', {
           tenantId: tenantId,
            bodyTenantId: requestBody.tenant_id,
            headers: {
              'x-tenant-id': tenantId
            }
        });

        console.log('[DEBUG] handleExecutePlaybook sending:', {
            url: `${API_BASE_URL}/api/answer`,
            body: requestBody,
            promptLength: !!prompt ? prompt.length : 0,
            hasQuestion: !!requestBody.question,
            sessionId: clientTraceId
        });

        const response = await fetch(`${API_BASE_URL}/api/answer`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...commonHeaders
            },
            credentials: 'include',
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Request failed: ${errorText}`);
        }

        const data = await response.json();

        const botMessage = {
            sender: 'bot',
            text: data.answer || "Playbook activated successfully.",
            id: nextMessageId()
        };
        setMessages(prev => [...prev, botMessage]);

        // Track playbook execution
        logNeoRecommendation({
          tenantId: getCurrentTenantId(currentUser, tenantId),
          userId: currentUser?.id || clientTraceId,
          accountId: playbookContext.account_id,
          accountName: playbookContext.account_name,
          playbookId: playbookContext.playbook_id,
          recommendationType: 'playbook',
          source: 'chatbot',
          metadata: {
            playbook_name: playbookContext.playbook_name
          }
        }).catch(err => console.error('Analytics error:', err));

    } catch (error) {
        console.error("Error executing playbook:", error);

        const errorMessage = {
            sender: 'bot',
            text: `Sorry, I encountered an error while executing the playbook. Please try again later.`,
            id: nextMessageId()
        };
        setMessages(prev => [...prev, errorMessage]);
    } finally {
        setIsSending(false);
    }
}, [currentUser, setMessages, setIsSending, clientTraceId, tenantId, commonHeaders]);

const handleShowTenders = useCallback(async () => {
  const userMessage = { sender: 'user', text: tenderPrompt, id: nextMessageId() };
  setMessages(prev => [...prev, userMessage]);
  setIsLoading(true);

  try {
    const effectiveTenantId =
      tenantId ||
      resolveChatPanelTenant(
        currentUser?.role === 'admin'
          ? (currentTenant || localStorage.getItem('admin-active-tenant'))
          : null,
        currentTenant,
        currentUser,
      );
    if (!effectiveTenantId) return;

    const primaryCategories = await fetchPrimaryProcurementCategoriesForTenant(effectiveTenantId);
    console.log('📋 Chatbot live tenders — primary categories:', primaryCategories);

    const liveDealsRaw = await fetchWithRetry(() =>
      fetchLiveDealsFromProcurementApi(effectiveTenantId, {
        categories: primaryCategories.length ? primaryCategories : null,
      })
    );

    console.log(`✅ Fetched ${liveDealsRaw.length} live deals from procurement API`);

    const activeLiveTenders = filterActiveLiveDeals(liveDealsRaw, primaryCategories);
    const categoryLabel =
      primaryCategories.length > 0 ? primaryCategories.join(', ') : 'all categories';

    let response = "";

    if (activeLiveTenders.length === 0) {
      response =
        primaryCategories.length > 0
          ? `No active live tenders found in your primary procurement categories (${categoryLabel}) at the moment.`
          : 'No active live tenders found at the moment.';
    } else {
      const topTenders = activeLiveTenders.slice(0, 10);
      response = `Here are the current **live tenders** in your primary categories (${categoryLabel}), showing up to ${topTenders.length}:\n\n`;

      topTenders.forEach((tender, index) => {
        const dl = getProcurementDeadline(tender);
        const dlStr = dl && dl !== 'N/A' ? new Date(dl).toLocaleDateString() : 'N/A';
        const categoryDisplay = getDealCategoryDisplay(tender) || tender.category || 'N/A';
        response += `${index + 1}. **${tender.title}**\n`;
        response += `   Institution: ${tender.institution || tender.account_name || tender.institution_name || 'N/A'}\n`;
        response += `   Deadline: ${dlStr}\n`;
        response += `   Category: ${categoryDisplay}\n`;
        if (tender.deal_type) response += `   Type: ${tender.deal_type}\n`;
        if (tender.estimated_value) {
          response += `   Value: ${tender.currency || 'AUD'} ${tender.estimated_value.toLocaleString()}\n`;
        }
        if (tender.source_url) response += `   [View Tender →](${tender.source_url})\n`;
        response += "\n";
      });

      if (activeLiveTenders.length > 10) {
        response += `\n_Showing ${topTenders.length} of ${activeLiveTenders.length} tenders._`;
      }
      response += `\n\nWould you like me to help you draft outreach for any of these opportunities?`;
    }

    const botMessage = { sender: 'bot', text: response, id: nextMessageId() };
    setMessages(prev => [...prev, botMessage]);

    // Track tender display
    logNeoRecommendation({
      tenantId: getCurrentTenantId(currentUser, tenantId),
      userId: currentUser?.id || clientTraceId,
      recommendationType: 'tenders',
      source: 'chatbot',
      metadata: {
        tenders_shown: activeLiveTenders.length
      }
    }).catch(err => console.error('Analytics error:', err));

  } catch (error) {
    console.error("❌ Error fetching tenders:", error);

    // More helpful error message for users
    const hint = error.message?.includes('exported') || error.message?.includes('list')
      ? 'There may be a configuration issue. Please refresh and try again, or contact support if the problem persists.'
      : error.message;

    const errorMessage = {
      sender: 'bot',
      text: t('neoChatErrorTenders', { hint }),
      id: nextMessageId()
    };
    setMessages(prev => [...prev, errorMessage]);
  } finally {
    setIsLoading(false);
  }
}, [setMessages, setIsLoading, tenantId, currentTenant, currentUser, clientTraceId, tenderPrompt, t]);

 const handleSend = useCallback(async (text, metadata = {}) => {
    console.log('[DEBUG] ========== handleSend CALL START ==========');
    const supplied = typeof text === 'string' ? text : '';
    const base = supplied.length ? supplied : (typeof input === 'string' ? input : '');
    const currentInput = (base ?? '').toString();
    const trimmed = currentInput.trim();

    console.log('[DEBUG] After parsing:', { supplied, base, currentInput, trimmed });

    if (!trimmed && !metadata.config) return;
    if (isSending) return;

    // Handle config from OutreachConfigurationForm
    if (metadata.config) {
        // Retrieve account and persona from clarification state, which should be 'configure_outreach' type
        const { account, persona } = clarification;
        const config = metadata.config;

        // Store this outreach context for the "Generate Another" button
        setLastOutreachContext({ account, persona });

        setClarification(null);
        setInput('');

        const userMessage = {
            sender: 'user',
            text: `Generate ${config.channel} outreach to ${persona.first_name} ${persona.last_name} at ${account.account_name}`,
            id: nextMessageId()
        };
        setMessages(prev => [...prev, userMessage]);
        setIsSending(true);

        try {
            const question = buildOutreachQuestion({ account, persona, config, userContext: config.userContext });
            const requestBody = {
                question,
                message_request: {
                    persona_name: `${persona.first_name} ${persona.last_name}`,
                    institution_name: account.account_name,
                    channel: config.channel,
                    tone: config.tone,
                    message_type: config.message_type,
                    context_type: config.context_type,
                    custom_message: config.userContext,
                    display_name: currentUser?.display_name || currentUser?.full_name
                },
                tenant_id: tenantId,
                access_level: currentUser?.access_level || DEFAULT_ACCESS,
                session_id: clientTraceId,
                user_id: currentUser?.id || clientTraceId,
                display_name: currentUser?.display_name || currentUser?.full_name
            };

            const response = await fetch(`${API_BASE_URL}/api/answer`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...commonHeaders
                },
                credentials: 'include',
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
            }

            const data = await response.json();

            let messageText = data.message || data.answer || "I couldn't generate the outreach message.";
            const userName = currentUser?.display_name || currentUser?.full_name || '';
            const companyName = currentUser?.company_name || '';

            if (data.sequence && Array.isArray(data.sequence)) {
                messageText = "**3-Message Sequence:**\n\n";
                data.sequence.forEach((step, index) => {
                    const dayLabel = step.wait_days !== undefined ? `Day ${step.wait_days}` : `Day ${index === 0 ? 0 : index === 1 ? 3 : 7}`;
                    const stepMessage = typeof step === 'object' && step !== null && step.message !== undefined ? step.message : step;

                    let processedMsg = stepMessage
                        .replace(/\[Your Name\]/g, userName)
                        .replace(/\[Your Contact Information\]/g, companyName)
                        .replace(/\[Your Position\],?\s*/g, '');

                    // 🔧 FIX: Ensure signature has proper line breaks in sequence messages
                    if (userName && companyName) {
                        // Pattern 1: Fix "Best, Tiago Smowl" → "Best,\nTiago\nSmowl"
                        processedMsg = processedMsg.replace(
                            new RegExp(`(Best|Thanks|Regards|Best regards|Kind regards|Warm regards|Sincerely|Cheers),\\s+${userName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s+${companyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*$`, 'gm'),
                            `$1,\n${userName}\n${companyName}`
                        );

                        // Pattern 2: Fix "Best,\nTiago Smowl" → "Best,\nTiago\nSmowl"
                        processedMsg = processedMsg.replace(
                            new RegExp(`(Best|Thanks|Regards|Best regards|Kind regards|Warm regards|Sincerely|Cheers),\\s*\\n\\s*${userName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s+${companyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*$`, 'gm'),
                            `$1,\n${userName}\n${companyName}`
                        );
                    }

                    messageText += `**${dayLabel} Message:**\n\n${processedMsg}\n\n---\n\n`;
                });
            } else {
                // Replace placeholders (preserving line breaks)
                messageText = messageText
                    .replace(/\[Your Name\]/g, userName)
                    .replace(/\[Your Contact Information\]/g, companyName)
                    .replace(/\[Your Position\],?\s*/g, '');

                // 🔧 FIX: Ensure signature has proper line breaks
                // Handle both squashed signatures and any missing line breaks
                if (userName && companyName) {
                    // Pattern 1: Fix "Best, Tiago Smowl" → "Best,\nTiago\nSmowl"
                    messageText = messageText.replace(
                        new RegExp(`(Best|Thanks|Regards|Best regards|Kind regards|Warm regards|Sincerely|Cheers),\\s+${userName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s+${companyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*$`, 'gm'),
                        `$1,\n${userName}\n${companyName}`
                    );

                    // Pattern 2: Fix "Best,\nTiago Smowl" → "Best,\nTiago\nSmowl"
                    messageText = messageText.replace(
                        new RegExp(`(Best|Thanks|Regards|Best regards|Kind regards|Warm regards|Sincerely|Cheers),\\s*\\n\\s*${userName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s+${companyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*$`, 'gm'),
                        `$1,\n${userName}\n${companyName}`
                    );
                }
            }

            const botMessage = {
              sender: 'bot',
              text: messageText,
              id: nextMessageId(),
              isOutreachMessage: true
            };
            setMessages(prev => [...prev, botMessage]);

            // Track outreach generation
            logNeoRecommendation({
              tenantId: getCurrentTenantId(currentUser, tenantId),
              userId: currentUser?.id || clientTraceId,
              accountId: extractAccountId(clarification.account),
              accountName: clarification.account?.account_name,
              personId: extractPersonId(clarification.persona),
              personName: `${clarification.persona.first_name} ${clarification.persona.last_name}`,
              recommendationType: 'outreach',
              channel: config.channel,
              source: 'chatbot',
              metadata: {
                message_type: config.message_type,
                tone: config.tone,
                context_type: config.context_type
              }
            }).catch(err => console.error('Analytics error:', err));

        } catch (error) {
            console.error("Error generating outreach:", error);
            const errorMessage = { sender: 'bot', text: t('neoChatErrorOutreach', { error: error.message }), id: nextMessageId() };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsSending(false);
        }
        return;
    }

    if (trimmed.toLowerCase() === tenderPrompt.toLowerCase()) {
      await handleShowTenders();
      setInput('');
      return;
    }

    const chip = suggestionChips.find(c => {
        if (c.prompt.includes('{')) {
            return currentInput === c.prompt;
        }
    return currentInput === c.prompt;
    });
    if (chip?.requires === 'account_and_persona') {
      // For "Draft outreach", now directly trigger the OutreachConfigPicker (selection step)
      setClarification({ type: 'select_outreach_target', originalPrompt: chip.prompt });
      return;
    }
    if (currentInput.includes('{account_name}')) {
      setClarification({ type: 'account', originalPrompt: currentInput });
      return;
    }

    const userMessage = { sender: 'user', text: currentInput, id: nextMessageId() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    await sendChatbotMessage(trimmed, { addUserMessage: false });
    }, [input, isSending, clarification, setClarification, setInput, setMessages, setIsSending, currentUser, handleShowTenders, clientTraceId, setLastOutreachContext, tenantId, commonHeaders, sendChatbotMessage, suggestionChips, tenderPrompt, t]);

  const handleAccountSelect = useCallback(async (account, originalPrompt) => {
  const briefStem = t('neoChatGiveBriefOn', { accountName: '' }).replace(/\{accountName\}/, '').trim().toLowerCase();
  const isBriefing =
    typeof originalPrompt === 'string' &&
    (originalPrompt.includes('{account_name}') ||
      originalPrompt === briefOnAccountTemplate ||
      originalPrompt.toLowerCase().startsWith(briefStem));

  if (isBriefing) {
    const userMessage = {
      sender: 'user',
      text: t('neoChatGiveBriefOn', { accountName: account.account_name }),
      id: nextMessageId()
    };
    setMessages(prev => [...prev, userMessage]);
    setClarification(null);
    setIsSending(true);

    try {
        // 🔍 ADD THIS DEBUG
        const adminTenantForLog =
          currentUser?.role === 'admin'
            ? localStorage.getItem('admin-active-tenant')
            : null;
        console.log('📤 About to call /api/debrief with:', {
          'tenantId variable': tenantId,
          'commonHeaders': commonHeaders,
          'account': {
            id: account.account_id || account.id,
            name: account.account_name
        },
        'adminTenant (admin-gated)': adminTenantForLog
      });
      const response = await fetch(`${API_BASE_URL}/api/debrief`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...commonHeaders
        },
        credentials: 'include',
        body: JSON.stringify({
          sessionId: clientTraceId,
          userId: currentUser?.id || clientTraceId,
          account_id: account.account_id || account.id,
          account_name: account.account_name,
          tenant_id: tenantId,
          language: resolvePlatformUiLanguage(currentUser, tenantId),
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        if (response.status === 404) {
          console.warn(`Account debrief not found for: ${account.account_name} (ID: ${account.id})`);
          const noDebriefMessage = {
            sender: 'bot',
            text: t('neoChatNoDebrief', { accountName: account.account_name }),
            id: nextMessageId()
          };
          setMessages(prev => [...prev, noDebriefMessage]);
          setIsSending(false);
          return;
        }
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

       const data = await response.json();

       const botMessage = {
         sender: 'bot',
         text: data.answer,
         id: nextMessageId()
       };
       setMessages(prev => [...prev, botMessage]);

       logBriefShown({
         tenantId: getCurrentTenantId(currentUser, tenantId),
         userId: currentUser?.id || clientTraceId,
         accountId: extractAccountId(account),
         accountName: account?.account_name,
         briefType: 'account',
         source: 'chatbot',
       }).catch(err => console.error('Analytics error:', err));

       // Track brief generation
       logNeoRecommendation({
         tenantId: getCurrentTenantId(currentUser, tenantId),
         userId: currentUser?.id || clientTraceId,
         accountId: extractAccountId(account),
         accountName: account?.account_name,
         recommendationType: 'brief',
         source: 'chatbot',
         promptText: t('neoChatGiveBriefOn', { accountName: account.account_name })
       }).catch(err => console.error('Analytics error:', err));

       } catch (error) {
         console.error("Error fetching debrief:", error);
         const errorMessage = {
           sender: 'bot',
           text: t('neoChatErrorAccountBrief', { error: error.message }),
           id: nextMessageId()
         };
         setMessages(prev => [...prev, errorMessage]);
       } finally {
         setIsSending(false);
       }
       return;
  }
  // Handle persona selection flow (for "Draft outreach" - the old way, now only for briefing persona selection)
  if (clarification?.nextStep === 'persona') {
    setIsLoading(true);
    try {
      let relatedPeople = [];
      // Filter the already loaded `people` array
      relatedPeople = people.filter((p) => personaBelongsToAccount(p, account));
    } catch (error) {
      console.error("Failed to fetch people:", error);
    }
    setIsLoading(false);
    setClarification(prev => ({
      ...prev,
      type: 'persona',
      account,
      nextStep: prev?.nextStep
    }));
    setInput('');
    if (!typingRef.current) scrollToBottom(true);
    focusInputIfNeeded();
    return;
  }

  const template = (typeof originalPrompt === 'string' && originalPrompt.length)
    ? originalPrompt
    : briefOnAccountTemplate;

  const newPrompt = template.replace('{account_name}', account.account_name);
  setClarification(null);
  handleSend(newPrompt);
}, [clarification, setIsLoading, people, setClarification, setInput, typingRef, scrollToBottom, focusInputIfNeeded, handleSend, currentUser, clientTraceId, setMessages, setIsSending, tenantId, commonHeaders, t, briefOnAccountTemplate]);

  const handlePersonaSelect = useCallback((persona) => {
      // This handlePersonaSelect is now primarily for the old "briefing" flow,
      // where persona is selected after account. For outreach, the new picker handles it.
      if (clarification?.originalPrompt?.includes('Draft an outreach')) {
          // This path should ideally not be hit if the new outreach flow is used.
          // If it is, it means a direct prompt like "Draft an outreach to John Doe at Acme" might have triggered the old flow.
          // In that case, we can redirect to the new configure_outreach step.
          setClarification(prev => ({
              type: 'configure_outreach',
              account: prev.account,
              persona: persona
          }));
      } else {
          // This handles cases like selecting a persona for a specific briefing detail
          setClarification(prev => ({
              ...prev,
              type: 'tone_and_context_removed',
              persona: persona
          }));
      }
      setInput('');
      if (!typingRef.current) scrollToBottom(true);
      focusInputIfNeeded();
  }, [clarification, setClarification, setInput, typingRef, scrollToBottom, focusInputIfNeeded]);

  useEffect(() => {
    console.log('🔍 Context changed:', context);

    if (context?.autoSendChatbot && context?.chatbotQuestion) {
      const requestKey = context.chatbotRequestId ?? context.chatbotQuestion;
      if (autoSendKeyRef.current === requestKey) return;
      autoSendKeyRef.current = requestKey;

      const question = context.chatbotQuestion;
      const nbm = sanitizeNextBestMoveForDisplay(context.chatbotContext?.next_best_move);
      setClarification(null);

      const initialMessages = [];
      if (nbm?.move) {
        initialMessages.push({
          sender: 'bot',
          type: 'next_best_move',
          nbm,
          id: nextMessageId(),
        });
      }
      initialMessages.push({
        sender: 'user',
        text: question,
        id: nextMessageId(),
      });
      setMessages(initialMessages);

      pendingAutoSendRef.current = {
        question,
        extraContext: context.chatbotContext ?? null,
        sessionId: context.chatbotSessionId ?? undefined,
        accountId: chatbotAskAccountIdFromSources({
          override: context.account_id,
          resolved: chatbotAskAccountId,
        }),
      };
      return;
    }

    if (context?.showNextBestMove) {
      const requestKey = context.chatbotRequestId ?? 'show-next-best-move';
      if (autoSendKeyRef.current === requestKey) return;
      autoSendKeyRef.current = requestKey;

      nbmViewActiveRef.current = true;
      setClarification(null);

      const optimisticNbm = sanitizeNextBestMoveForDisplay(
        context.chatbotContext?.next_best_move ?? context.next_best_move,
      );

      if (optimisticNbm?.move) {
        setMessages([
          {
            sender: 'bot',
            type: 'next_best_move',
            nbm: optimisticNbm,
            accountId: accountPageId ?? context.account_id,
            id: `nbm-${accountPageId ?? context.account_id ?? 'pending'}`,
          },
        ]);
      } else {
        setMessages([createDefaultGreetingMessage(language)]);
      }
      return;
    }

    if (context?.directToOutreach) {
      console.log('🎯 Direct outreach detected');

      if (context.preselectedAccount && context.preselectedPersona) {
          // If both are present, jump directly to the configuration form
          setClarification({
              type: 'configure_outreach',
              account: context.preselectedAccount,
              persona: context.preselectedPersona
          });
          const targetName = `${context.preselectedPersona.first_name} ${context.preselectedPersona.last_name}`;
          const initialMessage = {
              sender: 'user',
              text: `Draft outreach to ${targetName}`,
              id: nextMessageId()
          };
          setMessages([initialMessage]);
      } else {
          // If one or both are missing, go to the selection picker with what's available
          setClarification({
              type: 'select_outreach_target',
              originalPrompt: "Draft an outreach",
              preselectedAccount: context.preselectedAccount,
              preselectedPersona: context.preselectedPersona
          });
          const targetName = context.preselectedAccount?.account_name || "a target";
          const initialMessage = {
              sender: 'user',
              text: `Draft outreach for ${targetName}`,
              id: nextMessageId()
          };
          setMessages([initialMessage]);
      }
    }
    else if (context?.autoExecutePlaybook && context?.message && context?.playbookContext && !playbookExecuted) {
      console.log('🎯 Auto-executing playbook with context:', context.playbookContext);

      setPlaybookExecuted(true);

      const userMessage = { sender: 'user', text: context.message, id: nextMessageId() };
      console.log('💬 Adding user message:', userMessage.text);
      setMessages([userMessage]);

      setTimeout(() => {
        console.log('⏰ Executing playbook after delay');
        handleExecutePlaybook(context.playbookContext);
      }, 100);

    } else if (context?.triggerMeetingPrep || context?.prefilledPrompt) {
      const chip = context.prefilledPrompt
        ? suggestionChips.find((c) => {
            if (c.prompt.includes('{')) {
              return context.prefilledPrompt === c.prompt;
            }
            return context.prefilledPrompt === c.prompt;
          })
        : null;

      if (chip?.requires === 'meeting_prep' || context.triggerMeetingPrep) {
        setClarification({
          type: 'meeting_prep',
          initialAccount: context.preselectedAccount ?? null,
          salesMapPayload: context.salesMapPayload ?? null,
        });
        setInput('');
      } else if (context.prefilledPrompt && chip?.requires === 'account_and_persona') {
        // Direct prefilled prompt for outreach should go to the new selection picker
        setClarification({ type: 'select_outreach_target', originalPrompt: chip.prompt });
        setInput('');
      } else if (context.prefilledPrompt && context.prefilledPrompt.includes('{account_name}')) {
        setClarification({ type: 'account', originalPrompt: context.prefilledPrompt });
        setInput('');
      } else if (context.prefilledPrompt) {
        setInput(context.prefilledPrompt);
      }
    }
  }, [
    context,
    handleExecutePlaybook,
    playbookExecuted,
    setClarification,
    setMessages,
    setInput,
    suggestionChips,
  ]);

  // Must run after the context effect that populates pendingAutoSendRef.
  useEffect(() => {
    const pending = pendingAutoSendRef.current;
    if (!pending || !tenantId || !resolvedUserId) return;

    pendingAutoSendRef.current = null;
    if (pending.sessionId) {
      setChatSessionId(pending.sessionId);
    }

    sendChatbotMessage(pending.question, {
      addUserMessage: false,
      extraContext: pending.extraContext,
      sessionIdOverride: pending.sessionId,
      accountIdOverride: pending.accountId,
    });
  }, [
    tenantId,
    resolvedUserId,
    context?.chatbotRequestId,
    sendChatbotMessage,
    setChatSessionId,
  ]);

  const handleChipClick = useCallback((prompt, requires) => {
    if (requires === 'show_tenders') {
        handleShowTenders();
    } else if (requires === 'meeting_prep') {
        setClarification({ type: 'meeting_prep' });
        setInput('');
    } else if (requires === 'account_and_persona') {
        // Trigger the new outreach selection flow
        setClarification({ type: 'select_outreach_target', originalPrompt: prompt });
        setInput('');
    } else if (prompt.includes('{account_name}')) {
      setClarification({ type: 'account', originalPrompt: prompt });
      setInput('');
    } else {
      setInput(prompt);
    }
    if (!typingRef.current) scrollToBottom(true);
    focusInputIfNeeded();
  }, [handleShowTenders, setClarification, setInput, typingRef, scrollToBottom, focusInputIfNeeded]);

  const handleProactiveChipClick = useCallback((item, parentMessage) => {
    const userId = resolvedUserId;
    if (item?.id && userId) {
      postChatbotRecommendationAction(item.id, 'clicked', tenantId, userId);
    }

    if (parentMessage?.type === 'morning_brief') {
      if (item?.chipActionType && item.chipActionType !== 'navigate') return;
      const pagePath = buildBriefTargetPageUrl(item);
      if (pagePath) navigate(createPageUrl(pagePath));
      return;
    }

    const prompt = String(item?.chipPrompt || item?.chipLabel || '').trim();
    if (!prompt || isSending) return;

    if (parentMessage?.type === 'trigger_nudges' && parentMessage?.id) {
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== parentMessage.id || m.type !== 'trigger_nudges') return m;
          return {
            ...m,
            triggerItems: (m.triggerItems || []).map((t) =>
              t.id === item.id ? { ...t, clicked: true } : t,
            ),
          };
        }),
      );
    }

    setMessages((prev) => [...prev, { sender: 'user', text: prompt, id: nextMessageId() }]);
    sendChatbotMessage(prompt, { addUserMessage: false });
  }, [isSending, resolvedUserId, tenantId, sendChatbotMessage, setMessages, navigate]);

  const handleChatDeepLinkClick = useCallback((e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    const target = e.target;
    if (!(target instanceof Element)) return;
    const anchor = target.closest('a[href^="neogtm://"]');
    if (!anchor) return;
    e.preventDefault();
    const bubble = anchor.closest('[data-chat-message-id]');
    const msgId = bubble?.getAttribute('data-chat-message-id');
    const msg = msgId ? messages.find((m) => String(m.id) === msgId) : null;
    navigateToNeogtmDeepLink(anchor.getAttribute('href'), navigate, createPageUrl, {
      linkRegistry: msg?.linkRegistry,
      people,
      linkLabel: anchor.textContent,
    });
  }, [navigate, messages, people]);

  const eventTracking = useMemo(
    () => ({
      tenantId: getCurrentTenantId(currentUser, tenantId),
      userId: resolvedUserId || currentUser?.id,
    }),
    [currentUser, tenantId, resolvedUserId],
  );

  // Return with stable component references
  if (!tenantId) {
    return (
      <div className="flex items-center justify-center min-h-[200px] p-6 bg-white">
        <Loader2 className="w-6 h-6 animate-spin text-[#00C2C2]" aria-label={t('neoChatLoadingChat')} />
      </div>
    );
  }

  return isDesktop ? (
    <DesktopChatView
      messages={visibleMessages}
      isSending={isSending}
      isLoading={isLoading}
      clarification={clarification}
      accounts={accounts}
      isLoadingData={isLoadingData}
      people={people}
      currentUser={currentUser}
      input={input}
      setInput={setInput}
      composingRef={composingRef}
      typingRef={typingRef}
      contentRef={contentRef}
      bottomRef={bottomRef}
      desktopScrollRef={desktopScrollRef}
      inputRef={inputRef}
      handleChipClick={handleChipClick}
      handleSend={handleSend}
      handleExecutePlaybook={handleExecutePlaybook}
      handleAccountSelect={handleAccountSelect}
      handlePersonaSelect={handlePersonaSelect}
      setClarification={setClarification}
      scrollToBottom={scrollToBottom}
      focusInputIfNeeded={focusInputIfNeeded}
      onClose={onClose}
      handleNewChat={handleNewChat}
      onDisambiguationSelect={onDisambiguationSelect}
      onProactiveChipClick={handleProactiveChipClick}
      onChatDeepLinkClick={handleChatDeepLinkClick}
      onSuggestedNextClick={onSuggestedNextClick}
      suggestionChips={suggestionChips}
      setMessages={setMessages}
      lastOutreachContext={lastOutreachContext}
      setIsSending={setIsSending}
      commonHeaders={commonHeaders}
      tenantId={tenantId}
      chatSessionId={chatSessionId}
      eventTracking={eventTracking}
      accountPageId={accountPageId}
      selectedAccount={selectedAccount}
      onClearAnchor={onClearAnchor}
    />
  ) : (
    <MobileChatView
      messages={visibleMessages}
      isSending={isSending}
      isLoading={isLoading}
      clarification={clarification}
      accounts={accounts}
      people={people}
      isLoadingData={isLoadingData}
      currentUser={currentUser}
      input={input}
      setInput={setInput}
      composingRef={composingRef}
      typingRef={typingRef}
      contentRef={contentRef}
      bottomRef={bottomRef}
      mobileScrollRef={mobileScrollRef}
      inputRef={inputRef}
      handleChipClick={handleChipClick}
      handleSend={handleSend}
      handleExecutePlaybook={handleExecutePlaybook}
      handleAccountSelect={handleAccountSelect}
      handlePersonaSelect={handlePersonaSelect}
      setClarification={setClarification}
      scrollToBottom={scrollToBottom}
      focusInputIfNeeded={focusInputIfNeeded}
      onClose={onClose}
      handleNewChat={handleNewChat}
      onDisambiguationSelect={onDisambiguationSelect}
      onProactiveChipClick={handleProactiveChipClick}
      onChatDeepLinkClick={handleChatDeepLinkClick}
      onSuggestedNextClick={onSuggestedNextClick}
      suggestionChips={suggestionChips}
      setMessages={setMessages}
      lastOutreachContext={lastOutreachContext}
      setIsSending={setIsSending}
      commonHeaders={commonHeaders}
      tenantId={tenantId}
      chatSessionId={chatSessionId}
      eventTracking={eventTracking}
      accountPageId={accountPageId}
      selectedAccount={selectedAccount}
      onClearAnchor={onClearAnchor}
    />
  );
}